import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { useAuth } from '../../context/AuthContext';
import {
    ArrowLeft,
    Mail,
    Phone,
    Calendar,
    ShieldCheck,
    ShieldAlert,
    BookOpen,
    Clock,
    TrendingUp,
    Award,
    Activity,
    MapPin,
    Loader2,
    AlertCircle,
    CheckCircle2,
    X,
    HelpCircle,
    Eye,
    Check,
    Plus,
    Layers,
    Info,
    RotateCcw,
    GraduationCap,
    XCircle,
    Key,
    Send,
    Edit,
    DollarSign,
    CreditCard,
    Tag,
    FileText,
    ExternalLink,
    Receipt
} from 'lucide-react';
import { formatForDateTimeLocal, formatCSTDateDisplay } from '../../utils/paymentUtils';

export default function StudentDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [student, setStudent] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [deactivateMessage, setDeactivateMessage] = useState('');
    const [pendingCohortId, setPendingCohortId] = useState<string | null>(null);
    const [viewingQuizResult, setViewingQuizResult] = useState<any>(null);
    const [notification, _setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    const setNotification = (notif: { type: 'success' | 'error', message: string } | null) => {
        _setNotification(notif);
        if (notif) {
            Swal.fire({
                icon: notif.type,
                title: notif.type === 'success' ? 'Success' : 'Error',
                text: notif.message,
                confirmButtonColor: 'var(--index-primary-color)',
                timer: notif.type === 'success' ? 2500 : undefined,
                showConfirmButton: notif.type !== 'success'
            });
        }
    };

    const [sendingCredentials, setSendingCredentials] = useState(false);
    const [updatingPlan, setUpdatingPlan] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(false);

    // Software License Key (MSP) state
    const [mspKey, setMspKey] = useState('');
    const [savingKeys, setSavingKeys] = useState(false);

    // Cohort Assignment states
    const [showAssignCohortModal, setShowAssignCohortModal] = useState(false);
    const [allCohorts, setAllCohorts] = useState<any[]>([]);
    const [selectedCohortIds, setSelectedCohortIds] = useState<string[]>([]);
    const [loadingCohorts, setLoadingCohorts] = useState(false);
    const [assigningCohorts, setAssigningCohorts] = useState(false);

    // Certificate generation states
    const [certificates, setCertificates] = useState<any[]>([]);
    const [showIssueCertModal, setShowIssueCertModal] = useState(false);
    const [issuingCert, setIssuingCert] = useState(false);
    const [isReassign, setIsReassign] = useState(false);
    const [selectedCohortForCert, setSelectedCohortForCert] = useState<any>(null);
    const [viewingReceiptModal, setViewingReceiptModal] = useState<string | null>(null);
    const [certForm, setCertForm] = useState({
        fullName: '',
        courseTitle: '',
        issuedAt: '',
        issuedBy: ''
    });

    // Manual Receipt Email Modal States
    const [showSendReceiptModal, setShowSendReceiptModal] = useState(false);
    const [sendingReceipt, setSendingReceipt] = useState(false);
    const [receiptRecipientEmail, setReceiptRecipientEmail] = useState('');
    const [receiptCustomNote, setReceiptCustomNote] = useState('');
    const [receiptSendToStudent, setReceiptSendToStudent] = useState(false);

    const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api';

    const fetchStudentData = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/students/${id}`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            if (response.ok) {
                setStudent(data);
                setMspKey(data.msp_license_key || '');
            } else {
                throw new Error(data.message || 'Failed to retrieve student profile.');
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenSendReceiptModal = () => {
        if (!receiptRecipientEmail) {
            setReceiptRecipientEmail(user?.email || 'layosgroupllc@gmail.com');
        }
        setShowSendReceiptModal(true);
    };

    const handleSendReceiptEmail = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        if (!student) return;
        if (!receiptRecipientEmail.trim()) {
            Swal.fire({
                icon: 'warning',
                title: 'Recipient Required',
                text: 'Please enter at least one valid recipient email address.',
                confirmButtonColor: 'var(--index-primary-color)'
            });
            return;
        }

        setSendingReceipt(true);
        try {
            const response = await fetch(`${API_URL}/instructor/students/${id}/send-receipt-email`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    recipient_email: receiptRecipientEmail,
                    custom_note: receiptCustomNote,
                    send_to_student: receiptSendToStudent
                })
            });

            const data = await response.json();
            if (response.ok && data.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Receipt Dispatched!',
                    text: data.message || `Official payment receipt email sent to ${receiptRecipientEmail}`,
                    confirmButtonColor: 'var(--index-primary-color)'
                });
                setShowSendReceiptModal(false);
                setReceiptCustomNote('');
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Sending Failed',
                    text: data.message || 'Could not send payment receipt email.',
                    confirmButtonColor: 'var(--lgl-error)'
                });
            }
        } catch (err: any) {
            console.error('Send Receipt Error:', err);
            Swal.fire({
                icon: 'error',
                title: 'Network Error',
                text: err.message || 'Error communicating with server.',
                confirmButtonColor: 'var(--lgl-error)'
            });
        } finally {
            setSendingReceipt(false);
        }
    };

    const handleApprovePayment = async () => {
        if (!student) return;
        const result = await Swal.fire({
            title: 'Approve Registration & Payment',
            html: `
                <div style="text-align: left; font-size: 0.88rem; line-height: 1.6; color: var(--index-text-secondary);">
                    <p style="margin-bottom: 0.75rem;">This will mark the student's status as <strong style="color: var(--lgl-success);">Approved / Verified</strong> and unlock full platform features.</p>
                    <div style="margin-top: 1rem; padding: 0.85rem 1rem; background: var(--index-hover-bg); border-radius: 12px; border: 1.5px solid var(--index-border-subtle);">
                        <label style="display: flex; align-items: center; gap: 10px; cursor: pointer; font-weight: 750; font-size: 0.85rem; color: var(--index-text-heading);">
                            <input type="checkbox" id="swal-send-email" checked style="width: 17px; height: 17px; accent-color: var(--index-primary-color); cursor: pointer;" />
                            Send Welcome Email with login credentials to ${student.email}
                        </label>
                        <p style="margin: 4px 0 0 27px; font-size: 0.75rem; color: var(--index-text-faint);">Uncheck if you want to approve silently without emailing the student.</p>
                    </div>
                </div>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Confirm Approval',
            cancelButtonText: 'Cancel',
            confirmButtonColor: 'var(--lgl-success)',
            cancelButtonColor: 'var(--index-text-faint)',
            preConfirm: () => {
                const checkbox = document.getElementById('swal-send-email') as HTMLInputElement;
                return { sendEmail: checkbox ? checkbox.checked : false };
            }
        });

        if (!result.isConfirmed) return;
        const sendEmail = result.value?.sendEmail ?? false;

        setUpdatingStatus(true);
        try {
            const response = await fetch(`${API_URL}/instructor/students/${id}/approve-payment`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    send_email: sendEmail,
                    password: 'password123'
                })
            });
            const data = await response.json();
            if (response.ok) {
                setNotification({
                    type: 'success',
                    message: data.message || (sendEmail ? 'Registration approved and welcome email dispatched!' : 'Registration approved silently without sending email.')
                });
                fetchStudentData();
            } else {
                throw new Error(data.message || 'Failed to approve payment.');
            }
        } catch (err: any) {
            setNotification({ type: 'error', message: err.message });
        } finally {
            setUpdatingStatus(false);
            setTimeout(() => setNotification(null), 4000);
        }
    };

    const handleRejectPayment = async () => {
        if (!student) return;
        const result = await Swal.fire({
            title: 'Decline / Reject Registration?',
            text: `This will mark the student's status as Rejected and lock platform features for ${student.name}.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Reject Payment',
            cancelButtonText: 'Cancel',
            confirmButtonColor: 'var(--lgl-error)',
            cancelButtonColor: 'var(--index-text-faint)'
        });

        if (!result.isConfirmed) return;

        setUpdatingStatus(true);
        try {
            const response = await fetch(`${API_URL}/instructor/students/${id}/reject-payment`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            if (response.ok) {
                setNotification({ type: 'success', message: data.message || 'Registration marked as rejected.' });
                fetchStudentData();
            } else {
                throw new Error(data.message || 'Failed to reject payment.');
            }
        } catch (err: any) {
            setNotification({ type: 'error', message: err.message });
        } finally {
            setUpdatingStatus(false);
            setTimeout(() => setNotification(null), 4000);
        }
    };

    const handleChangePaymentStatus = async (newStatus: string) => {
        if (!student || student.payment_status === newStatus) return;

        setUpdatingStatus(true);
        try {
            const response = await fetch(`${API_URL}/students/${id}`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    name: student.name,
                    email: student.email,
                    payment_status: newStatus,
                    payment_plan: student.payment_plan || 'full',
                    payment_tracking_enabled: (student.payment_plan === 'installment' || student.payment_plan === '50_percent' || student.payment_plan === '50%'),
                    cohorts: student.cohorts?.map((c: any) => c.id) || []
                })
            });
            const data = await response.json();
            if (response.ok) {
                setNotification({
                    type: 'success',
                    message: `Registration status updated to "${newStatus.toUpperCase()}".`
                });
                fetchStudentData();
            } else {
                throw new Error(data.message || 'Failed to update payment status.');
            }
        } catch (err: any) {
            setNotification({ type: 'error', message: err.message });
        } finally {
            setUpdatingStatus(false);
            setTimeout(() => setNotification(null), 4000);
        }
    };

    const handleSendCredentials = async () => {
        if (!student) return;
        const result = await Swal.fire({
            title: 'Send Login Credentials?',
            text: `This will set the temporary password to "password123" and email login instructions to ${student.email}.`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Send Email',
            cancelButtonText: 'Cancel',
            confirmButtonColor: 'var(--index-primary-color)'
        });

        if (!result.isConfirmed) return;

        setSendingCredentials(true);
        try {
            const response = await fetch(`${API_URL}/instructor/students/${id}/send-credentials`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ password: 'password123' })
            });
            const data = await response.json();
            if (response.ok) {
                setNotification({ type: 'success', message: data.message || 'Login credentials email dispatched successfully!' });
            } else {
                throw new Error(data.message || 'Failed to send credentials.');
            }
        } catch (err: any) {
            setNotification({ type: 'error', message: err.message });
        } finally {
            setSendingCredentials(false);
            setTimeout(() => setNotification(null), 4000);
        }
    };

    const [updating2faExempt, setUpdating2faExempt] = useState(false);

    const handleToggle2faExemption = async () => {
        if (!student) return;
        const currentExempt = !!student.two_factor_exempt;
        const actionText = currentExempt ? 'Enforce Mandatory 2FA' : 'Grant 2FA Exemption';
        const result = await Swal.fire({
            title: `${actionText}?`,
            text: currentExempt
                ? `Revoke special 2FA exemption for ${student.name}? The student will be required to configure 2FA upon next login.`
                : `Grant special 2FA exemption for ${student.name}? The student will be allowed to access their account and coursework without 2FA.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: actionText,
            cancelButtonText: 'Cancel',
            confirmButtonColor: currentExempt ? '#dc2626' : '#0F5132'
        });

        if (!result.isConfirmed) return;

        setUpdating2faExempt(true);
        try {
            const response = await fetch(`${API_URL}/instructor/students/${id}/toggle-2fa-exemption`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ exempt: !currentExempt })
            });
            const data = await response.json();
            if (response.ok) {
                setNotification({ type: 'success', message: data.message || '2FA Exemption status updated.' });
                fetchStudentData();
            } else {
                throw new Error(data.message || 'Failed to update 2FA exemption.');
            }
        } catch (err: any) {
            setNotification({ type: 'error', message: err.message });
        } finally {
            setUpdating2faExempt(false);
            setTimeout(() => setNotification(null), 4000);
        }
    };

    const handleTogglePaymentPlan = async (newPlan: 'full' | 'installment') => {
        if (!student) return;
        const isInstallment = newPlan === 'installment';
        const result = await Swal.fire({
            title: isInstallment ? 'Switch to 50% Installment Plan?' : 'Mark as 100% Fully Paid?',
            text: isInstallment
                ? 'This will enable tuition tracking and activate the deadline reminder on the student dashboard.'
                : 'This will mark the student as fully paid and clear the tuition reminder countdown.',
            icon: 'info',
            showCancelButton: true,
            confirmButtonText: isInstallment ? 'Enable 50% Tracking' : 'Confirm 100% Paid',
            cancelButtonText: 'Cancel',
            confirmButtonColor: isInstallment ? '#d97706' : 'var(--lgl-success)'
        });

        if (!result.isConfirmed) return;

        setUpdatingPlan(true);
        try {
            const response = await fetch(`${API_URL}/students/${id}`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    name: student.name,
                    email: student.email,
                    payment_plan: newPlan,
                    payment_tracking_enabled: isInstallment,
                    payment_status: 'approved',
                    cohorts: student.cohorts?.map((c: any) => c.id) || []
                })
            });
            const data = await response.json();
            if (response.ok) {
                setNotification({
                    type: 'success',
                    message: isInstallment
                        ? 'Updated to 50% Installment Plan. Tuition reminder active.'
                        : 'Updated to 100% Full Payment. Tuition reminder cleared!'
                });
                fetchStudentData();
            } else {
                throw new Error(data.message || 'Failed to update payment plan.');
            }
        } catch (err: any) {
            setNotification({ type: 'error', message: err.message });
        } finally {
            setUpdatingPlan(false);
            setTimeout(() => setNotification(null), 4000);
        }
    };

    const handleEditCustomPaymentLink = async () => {
        if (!student) return;
        const currentLink = student.custom_payment_link || '';
        const currentAmount = student.custom_payment_amount !== null && student.custom_payment_amount !== undefined ? student.custom_payment_amount.toString() : '';
        const currentDeadline = formatForDateTimeLocal(student.payment_deadline);

        const { value: formValues, isConfirmed } = await Swal.fire({
            title: 'Payment Tracker & Expiration Override',
            html: `
                <div style="text-align: left; font-family: inherit; font-size: 0.88rem;">
                    <p style="color: #64748b; font-size: 0.8rem; margin-bottom: 1.15rem; line-height: 1.45;">
                        Configure custom payment tracker settings for this student. You can set a custom expiration/deadline date (CST), custom 50% balance amount, and checkout URL. Other students will remain on their standard course deadlines.
                    </p>

                    <label style="display: block; font-weight: 750; color: #1e293b; margin-bottom: 4px;">
                        Custom Expiration Date & Time (Central Time - CST)
                    </label>
                    <input id="swal-payment-deadline" type="datetime-local" class="swal2-input" value="${currentDeadline}" style="width: 100%; margin: 0 0 0.35rem 0; font-size: 0.85rem; box-sizing: border-box;" />
                    <p style="color: #64748b; font-size: 0.74rem; margin: 0 0 1rem 0; line-height: 1.35;">
                        Leave blank to use standard course/cohort expiration. Setting this date customizes this student's access countdown in CST (Central Time).
                    </p>

                    <label style="display: block; font-weight: 750; color: #1e293b; margin-bottom: 4px;">
                        Custom Payment URL (Square/Stripe Link)
                    </label>
                    <input id="swal-custom-link" class="swal2-input" placeholder="https://square.link/u/..." value="${currentLink}" style="width: 100%; margin: 0 0 1rem 0; font-size: 0.85rem; box-sizing: border-box;" />

                    <label style="display: block; font-weight: 750; color: #1e293b; margin-bottom: 4px;">
                        50% Balance Amount to Pay ($ USD)
                    </label>
                    <input id="swal-custom-amount" type="number" step="0.01" class="swal2-input" placeholder="e.g. 600, 900, 500" value="${currentAmount}" style="width: 100%; margin: 0 0 0.5rem 0; font-size: 0.85rem; box-sizing: border-box;" />
                </div>
            `,
            focusConfirm: false,
            showCancelButton: true,
            confirmButtonText: 'Save Payment Details',
            cancelButtonText: 'Cancel',
            confirmButtonColor: 'var(--index-primary-color)',
            preConfirm: () => {
                const linkInput = (document.getElementById('swal-custom-link') as HTMLInputElement)?.value.trim();
                const amountInput = (document.getElementById('swal-custom-amount') as HTMLInputElement)?.value.trim();
                const deadlineInput = (document.getElementById('swal-payment-deadline') as HTMLInputElement)?.value.trim();

                if (linkInput && !linkInput.startsWith('http://') && !linkInput.startsWith('https://')) {
                    Swal.showValidationMessage('Please enter a valid URL beginning with http:// or https://');
                    return false;
                }

                if (amountInput && (isNaN(Number(amountInput)) || Number(amountInput) < 0)) {
                    Swal.showValidationMessage('Please enter a valid positive payment amount');
                    return false;
                }

                const formattedDeadline = deadlineInput
                    ? (deadlineInput.includes('T') ? `${deadlineInput}:00-05:00` : `${deadlineInput} 23:59:59-05:00`)
                    : null;

                return {
                    custom_payment_link: linkInput || null,
                    custom_payment_amount: amountInput ? Number(amountInput) : null,
                    payment_deadline: formattedDeadline,
                };
            }
        });

        if (!isConfirmed || !formValues) return;

        setUpdatingPlan(true);
        try {
            const response = await fetch(`${API_URL}/instructor/students/${id}/custom-payment-link`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(formValues)
            });
            const data = await response.json();
            if (response.ok) {
                const hasOverrides = formValues.custom_payment_link || formValues.custom_payment_amount !== null || formValues.payment_deadline !== null;
                setNotification({
                    type: 'success',
                    message: hasOverrides
                        ? 'Custom payment tracker & expiration details saved successfully!'
                        : 'Reverted to automatic default payment flow.'
                });
                fetchStudentData();
            } else {
                throw new Error(data.message || 'Failed to update payment details.');
            }
        } catch (err: any) {
            setNotification({ type: 'error', message: err.message });
        } finally {
            setUpdatingPlan(false);
            setTimeout(() => setNotification(null), 4000);
        }
    };

    const handleClearCustomPaymentLink = async () => {
        if (!student) return;
        const result = await Swal.fire({
            title: 'Reset to Automatic Flow?',
            text: 'This will remove custom expiration date, link, and amount overrides and restore the automatic course-based payment flow for this student.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Reset to Default',
            cancelButtonText: 'Cancel',
            confirmButtonColor: 'var(--index-primary-color)'
        });

        if (!result.isConfirmed) return;

        setUpdatingPlan(true);
        try {
            const response = await fetch(`${API_URL}/instructor/students/${id}/custom-payment-link`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ custom_payment_link: null, custom_payment_amount: null, payment_deadline: null })
            });
            const data = await response.json();
            if (response.ok) {
                setNotification({
                    type: 'success',
                    message: 'Reverted to automatic default payment flow.'
                });
                fetchStudentData();
            } else {
                throw new Error(data.message || 'Failed to clear custom overrides.');
            }
        } catch (err: any) {
            setNotification({ type: 'error', message: err.message });
        } finally {
            setUpdatingPlan(false);
            setTimeout(() => setNotification(null), 4000);
        }
    };

    const fetchCertificates = async () => {
        try {
            const response = await fetch(`${API_URL}/certificates`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            if (response.ok) {
                const studentCerts = data.filter((c: any) => c.user_id === parseInt(id || ''));
                setCertificates(studentCerts);
            }
        } catch (err) {
            console.error("Error fetching certificates:", err);
        }
    };

    const handleOpenIssueCertModal = (cohort: any) => {
        setIsReassign(false);
        setSelectedCohortForCert(cohort);
        setCertForm({
            fullName: student?.name || '',
            courseTitle: cohort.course?.title || '',
            issuedAt: new Date().toISOString().substring(0, 10),
            issuedBy: user?.name || 'Instructor'
        });
        setShowIssueCertModal(true);
    };

    const handleOpenReassignModal = (cohort: any, existingCert: any) => {
        setIsReassign(true);
        setSelectedCohortForCert(cohort);
        setCertForm({
            fullName: existingCert.full_name || student?.name || '',
            courseTitle: existingCert.course_title || cohort.course?.title || '',
            issuedAt: existingCert.issued_at?.substring(0, 10) || new Date().toISOString().substring(0, 10),
            issuedBy: existingCert.issued_by || user?.name || 'Instructor'
        });
        setShowIssueCertModal(true);
    };

    const handleIssueCertificate = async () => {
        if (!selectedCohortForCert) return;
        setIssuingCert(true);
        try {
            const response = await fetch(`${API_URL}/instructor/certificates/generate-manual`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    course_id: selectedCohortForCert.course_id || selectedCohortForCert.course?.id,
                    cohort_id: selectedCohortForCert.id,
                    user_id: student.id,
                    full_name: certForm.fullName,
                    course_title: certForm.courseTitle,
                    issued_at: certForm.issuedAt,
                    issued_by: certForm.issuedBy
                })
            });
            const data = await response.json();
            if (response.ok) {
                setNotification({ type: 'success', message: isReassign ? 'Certificate reassigned successfully! Student will receive a new email.' : 'Certificate generated and issued successfully!' });
                fetchCertificates();
                setShowIssueCertModal(false);
            } else {
                throw new Error(data.message || 'Failed to issue certificate.');
            }
        } catch (err: any) {
            setNotification({ type: 'error', message: err.message });
        } finally {
            setIssuingCert(false);
            setTimeout(() => setNotification(null), 4000);
        }
    };

    const fetchCohorts = async () => {
        setLoadingCohorts(true);
        try {
            const response = await fetch(`${API_URL}/cohorts`, {
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            if (response.ok) {
                setAllCohorts(data);
            } else {
                throw new Error(data.message || 'Failed to load cohorts.');
            }
        } catch (err: any) {
            console.error('Error fetching cohorts:', err);
            setNotification({ type: 'error', message: err.message || 'Error loading available cohorts.' });
            setTimeout(() => setNotification(null), 4000);
        } finally {
            setLoadingCohorts(false);
        }
    };

    const handleAssignCohorts = async () => {
        if (selectedCohortIds.length === 0) return;
        setAssigningCohorts(true);
        try {
            const response = await fetch(`${API_URL}/instructor/students/${id}/assign-cohorts`, {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ cohorts: selectedCohortIds })
            });
            const data = await response.json();
            if (response.ok) {
                setNotification({ type: 'success', message: data.message || 'Cohorts assigned successfully!' });
                fetchStudentData();
                setShowAssignCohortModal(false);
                setSelectedCohortIds([]);
            } else {
                throw new Error(data.message || 'Failed to assign cohorts.');
            }
        } catch (err: any) {
            setNotification({ type: 'error', message: err.message });
        } finally {
            setAssigningCohorts(false);
            setTimeout(() => setNotification(null), 4000);
        }
    };

    const handleSaveLicenseKeys = async () => {
        setSavingKeys(true);
        try {
            const response = await fetch(`${API_URL}/instructor/students/${id}/license-keys`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    msp_license_key: mspKey.trim() || null
                })
            });
            const data = await response.json();
            if (response.ok) {
                setNotification({ type: 'success', message: 'Microsoft Project license key saved successfully!' });
                if (data.student) {
                    setStudent(data.student);
                    setMspKey(data.student.msp_license_key || '');
                }
            } else {
                throw new Error(data.message || 'Failed to save license key.');
            }
        } catch (err: any) {
            setNotification({ type: 'error', message: err.message || 'Error saving license keys.' });
        } finally {
            setSavingKeys(false);
            setTimeout(() => setNotification(null), 4000);
        }
    };

    const toggleActivation = async (cohortId: string, currentStatus: string, message?: string) => {
        const newStatus = currentStatus === 'inactive' ? 'active' : 'inactive';

        // If deactivating and no message yet, show modal
        if (newStatus === 'inactive' && !message) {
            setPendingCohortId(cohortId);
            setShowDeactivateModal(true);
            return;
        }

        try {
            const response = await fetch(`${API_URL}/cohorts/${cohortId}/students/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    status: newStatus,
                    message: message
                })
            });

            if (response.ok) {
                setShowDeactivateModal(false);
                setDeactivateMessage('');
                setPendingCohortId(null);
                setNotification({ type: 'success', message: `Student status updated to ${newStatus}.` });
                fetchStudentData();
                setTimeout(() => setNotification(null), 4000);
            } else {
                const data = await response.json();
                setNotification({ type: 'error', message: data.message || 'Failed to update access status.' });
                setTimeout(() => setNotification(null), 4000);
            }
        } catch (err) {
            console.error('Toggle status error:', err);
            setNotification({ type: 'error', message: 'An error occurred while updating access.' });
            setTimeout(() => setNotification(null), 4000);
        }
    };

    const confirmDeactivation = () => {
        if (pendingCohortId) {
            toggleActivation(pendingCohortId, 'active', deactivateMessage);
        }
    };

    const [expandedCohortMap, setExpandedCohortMap] = useState<Record<string, boolean>>({});

    const toggleLessonCompletion = async (lessonId: string, currentCompleted: boolean) => {
        try {
            const response = await fetch(`${API_URL}/lessons/${lessonId}/complete`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    student_id: student.id,
                    completed: !currentCompleted
                })
            });
            const data = await response.json();
            if (response.ok) {
                setNotification({ type: 'success', message: 'Lesson progress updated successfully.' });
                fetchStudentData(); 
            } else {
                setNotification({ type: 'error', message: data.message || 'Failed to update lesson progress.' });
            }
        } catch (err) {
            setNotification({ type: 'error', message: 'Network error updating progress.' });
        }
    };

    useEffect(() => {
        fetchStudentData();
        fetchCertificates();
    }, [id]);

    // Derived Metrics
    const validCohorts = student?.cohorts || [];
    const avgCompletion = validCohorts.length > 0
        ? Math.round(validCohorts.reduce((acc: number, c: any) => acc + Number(c.pivot?.progress || 0), 0) / validCohorts.length)
        : 0;

    const scoredLessons = student?.completed_lessons?.filter((l: any) => l.pivot?.score != null) || [];
    const avgQuizScore = scoredLessons.length > 0 
        ? Math.round(scoredLessons.reduce((acc: number, l: any) => acc + Number(l.pivot?.score), 0) / scoredLessons.length) 
        : 'N/A';

    return (
        <div className="student-details-container">
            <style>{`
                .receipt-thumbnail-hover:hover .receipt-overlay {
                    opacity: 1 !important;
                }
                .receipt-thumbnail-hover:hover img {
                    transform: scale(1.05);
                }
                .receipt-thumbnail-hover img {
                    transition: transform 0.3s ease;
                }
                .approve-btn-premium:hover {
                    transform: translateY(-2px);
                    background: var(--lgl-success) !important;
                    box-shadow: 0 6px 16px color-mix(in srgb, var(--index-primary-color) 30%, transparent) !important;
                }
                .approve-btn-premium:active {
                    transform: translateY(0);
                }
                .reject-btn-premium:hover {
                    background: var(--index-danger-bg-soft) !important;
                    border-color: color-mix(in srgb, var(--lgl-error) 45%, transparent) !important;
                }

                .staff-scope .student-details-container {
                    max-width: 1200px;
                    margin: 0 auto;
                    font-family: 'Inter', system-ui, -apple-system, sans-serif;
                }

                .staff-scope .back-link {
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    color: var(--index-text-secondary);
                    text-decoration: none;
                    font-weight: 800;
                    font-size: 0.9rem;
                    margin-bottom: 2rem;
                    transition: all 0.3s;
                    background: none;
                    border: none;
                    cursor: pointer;
                    padding: 0;
                }

                .back-link:hover {
                    color: var(--index-text-heading);
                    transform: translateX(-4px);
                }

                .staff-scope .profile-header-premium {
                    background: white;
                    border: 1.5px solid var(--index-hover-bg);
                    border-radius: 32px;
                    padding: 3rem;
                    display: flex;
                    gap: 3rem;
                    align-items: center;
                    margin-bottom: 2.5rem;
                    box-shadow: 0 10px 25px -5px rgba(0,0,0,0.02);
                }

                .staff-scope .avatar-massive {
                    width: 140px;
                    height: 140px;
                    background: linear-gradient(135deg, var(--index-primary-color), var(--index-primary-hover));
                    border-radius: 45px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: white;
                    font-size: 3.5rem;
                    font-weight: 950;
                    box-shadow: 0 20px 40px -10px color-mix(in srgb, var(--index-primary-color) calc(0.3 * 100%), transparent);
                }

                .profile-info h1 {
                    margin: 0 0 0.5rem 0;
                    font-size: 2.5rem;
                    font-weight: 950;
                    color: var(--index-text-heading);
                    letter-spacing: -0.04em;
                }

                .staff-scope .status-pill {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 6px 16px;
                    background: color-mix(in srgb, var(--lgl-success) 12%, transparent);
                    color: var(--index-primary-color);
                    border-radius: 12px;
                    font-size: 0.85rem;
                    font-weight: 950;
                    margin-bottom: 1.5rem;
                }

                .staff-scope .contact-grid-mini {
                    display: flex;
                    gap: 2rem;
                    flex-wrap: wrap;
                }

                .staff-scope .contact-item-mini {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    color: var(--index-text-secondary);
                    font-size: 0.95rem;
                    font-weight: 600;
                }

                .staff-scope .details-layout {
                    display: grid;
                    grid-template-columns: 1fr 0.4fr;
                    gap: 2.5rem;
                }

                .staff-scope .card-premium-records {
                    background: white;
                    border: 1.5px solid var(--index-hover-bg);
                    border-radius: 28px;
                    padding: 2.5rem;
                    margin-bottom: 2.5rem;
                }

                .staff-scope .card-title-records {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 2rem;
                }

                .card-title-records h3 {
                    margin: 0;
                    font-size: 1.25rem;
                    font-weight: 950;
                    color: var(--index-text-heading);
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .staff-scope .enrollment-row {
                    display: flex;
                    align-items: center;
                    gap: 1.5rem;
                    padding: 1.5rem;
                    background: var(--index-card-bg);
                    border: 1.5px solid var(--index-hover-bg);
                    border-radius: 20px;
                    margin-bottom: 1rem;
                    transition: all 0.3s;
                }

                .enrollment-row:hover {
                    border-color: var(--index-primary-color);
                    background: white;
                    transform: translateY(-2px);
                }

                .staff-scope .progress-ring-mini {
                    width: 50px;
                    height: 50px;
                    border-radius: 14px;
                    background: color-mix(in srgb, var(--lgl-success) 12%, transparent);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: var(--index-primary-color);
                    font-weight: 950;
                    font-size: 0.9rem;
                }

                .staff-scope .activity-item {
                    display: flex;
                    gap: 16px;
                    padding-bottom: 1.5rem;
                    border-left: 2px solid var(--index-hover-bg);
                    margin-left: 10px;
                    padding-left: 20px;
                    position: relative;
                }

                .activity-item::before {
                    content: '';
                    position: absolute;
                    left: -7px;
                    top: 0;
                    width: 12px;
                    height: 12px;
                    background: white;
                    border: 2px solid var(--index-primary-color);
                    border-radius: 50%;
                }

                .activity-content div {
                    font-weight: 850;
                    color: var(--index-text-heading);
                    font-size: 0.95rem;
                }

                .activity-content span {
                    color: var(--index-text-faint);
                    font-size: 0.8rem;
                    font-weight: 700;
                }

                .staff-scope .btn-secondary-outline {
                    background: transparent;
                    color: var(--index-text-secondary);
                    border: 1.5px solid var(--index-hover-bg);
                    padding: 0.6rem 1.2rem;
                    border-radius: 12px;
                    font-weight: 800;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.3s;
                    font-size: 0.85rem;
                }

                .btn-secondary-outline:hover {
                    background: var(--index-hover-bg);
                    border-color: var(--index-text-faint);
                }

                .staff-scope .btn-toggle-active {
                    background: color-mix(in srgb, var(--lgl-success) 12%, transparent);
                    color: var(--index-primary-color);
                    border: 1.5px solid color-mix(in srgb, var(--lgl-success) 15%, transparent);
                    padding: 0.6rem 1.2rem;
                    border-radius: 12px;
                    font-weight: 800;
                    cursor: pointer;
                    transition: all 0.3s;
                    font-size: 0.85rem;
                }

                .btn-toggle-active:hover {
                    background: color-mix(in srgb, var(--lgl-success) 15%, transparent);
                }

                .staff-scope .btn-toggle-inactive {
                    background: var(--index-danger-bg-soft);
                    color: var(--lgl-error);
                    border: 1.5px solid var(--index-danger-bg-soft);
                    padding: 0.6rem 1.2rem;
                    border-radius: 12px;
                    font-weight: 800;
                    cursor: pointer;
                    transition: all 0.3s;
                    font-size: 0.85rem;
                }

                .btn-toggle-inactive:hover {
                    background: var(--index-danger-bg-soft);
                }

                .staff-scope .modal-overlay {
                    position: fixed;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    background: rgba(0, 0, 0, 0.5);
                    backdrop-filter: blur(4px);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 1000;
                }

                .staff-scope .modal-box {
                    background: white;
                    padding: 2.5rem;
                    border-radius: 28px;
                    width: 100%;
                    max-width: 500px;
                    box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
                }

                .modal-box h3 {
                    margin: 0 0 1rem 0;
                    font-weight: 950;
                    color: var(--index-text-heading);
                    font-size: 1.5rem;
                }

                .modal-box textarea {
                    width: 100%;
                    height: 120px;
                    padding: 1rem;
                    border: 1.5px solid var(--index-hover-bg);
                    border-radius: 16px;
                    margin-bottom: 1.5rem;
                    font-family: inherit;
                    resize: none;
                }

                .modal-box textarea:focus {
                    outline: none;
                    border-color: var(--index-primary-color);
                }

                .staff-scope .modal-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                }

                .staff-scope .btn-confirm {
                    background: var(--lgl-error);
                    color: white;
                    border: none;
                    padding: 0.75rem 1.5rem;
                    border-radius: 12px;
                    font-weight: 800;
                    cursor: pointer;
                }

                .staff-scope .btn-cancel {
                    background: var(--index-hover-bg);
                    color: var(--index-text-secondary);
                    border: none;
                    padding: 0.75rem 1.5rem;
                    border-radius: 12px;
                    font-weight: 800;
                    cursor: pointer;
                }

                @media (max-width: 1024px) {
                    .staff-scope .profile-header-premium {
                        padding: 2rem;
                        gap: 2rem;
                    }
                    .staff-scope .avatar-massive {
                        width: 100px;
                        height: 100px;
                        font-size: 2.5rem;
                        border-radius: 32px;
                    }
                    .profile-info h1 {
                        font-size: 2rem;
                    }
                    .staff-scope .details-layout {
                        grid-template-columns: 1fr;
                    }
                }

                @media (max-width: 768px) {
                    .staff-scope .profile-header-premium {
                        flex-direction: column;
                        text-align: center;
                        padding: 2.5rem 1.5rem;
                    }
                    .staff-scope .contact-grid-mini {
                        justify-content: center;
                    }
                    .staff-scope .enrollment-row {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 1rem;
                    }
                    .enrollment-row > div:last-child {
                        width: 100%;
                        display: flex;
                        flex-direction: column;
                        gap: 0.75rem;
                    }
                    .enrollment-row button {
                        width: 100%;
                        justify-content: center;
                        height: 48px;
                    }
                    .staff-scope .card-premium-records {
                        padding: 1.5rem;
                    }
                    .staff-scope .metrics-grid {
                        grid-template-columns: 1fr !important;
                    }
                    .staff-scope .stat-box-mini {
                        min-width: 100%;
                    }
                }

                @media (max-width: 480px) {
                    .profile-info h1 {
                        font-size: 1.5rem;
                    }
                    .staff-scope .contact-grid-mini {
                        gap: 1rem;
                    }
                    .staff-scope .avatar-massive {
                        width: 80px;
                        height: 80px;
                        font-size: 2rem;
                    }
                }
            `}</style>

            <button onClick={() => navigate(-1)} className="back-link">
                <ArrowLeft size={18} /> Back to Students
            </button>

            {notification && (
                <div className="animate-slide-in" style={{
                    position: 'fixed',
                    top: '2rem',
                    right: '2rem',
                    zIndex: 1000,
                    padding: '1rem 1.5rem',
                    background: notification.type === 'success' ? 'color-mix(in srgb, var(--lgl-success) 12%, transparent)' : 'var(--index-danger-bg-soft)',
                    border: `1px solid ${notification.type === 'success' ? 'color-mix(in srgb, var(--lgl-success) 35%, transparent)' : 'var(--index-danger-bg-soft)'}`,
                    color: notification.type === 'success' ? 'var(--lgl-success)' : 'var(--lgl-error)',
                    borderRadius: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
                    fontWeight: 700
                }}>
                    {notification.type === 'success' ? <CheckCircle2 size={20} /> : <AlertCircle size={20} />}
                    <span>{notification.message}</span>
                    <button onClick={() => setNotification(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', display: 'flex' }}>
                        <X size={16} color={notification.type === 'success' ? 'var(--lgl-success)' : 'var(--lgl-error)'} />
                    </button>
                </div>
            )}

            {loading ? (
                <div style={{ padding: '8rem 0', textAlign: 'center' }}>
                    <Loader2 className="animate-spin" size={48} color="var(--index-primary-color)" style={{ margin: '0 auto' }} />
                    <p style={{ marginTop: '1.5rem', fontWeight: 800, color: 'var(--index-text-secondary)' }}>Loading Profile...</p>
                </div>
            ) : error || !student ? (
                <div style={{ padding: '4rem', background: 'var(--index-danger-bg-soft)', borderRadius: '32px', border: '1.5px solid var(--index-danger-bg-soft)', textAlign: 'center' }}>
                    <AlertCircle size={40} color="var(--lgl-error)" style={{ margin: '0 auto 1rem' }} />
                    <h3 style={{ margin: 0, color: 'var(--index-text-heading)', fontWeight: 950 }}>Student Not Found</h3>
                    <p style={{ color: 'var(--index-text-secondary)', fontWeight: 600, margin: '8px 0 2rem' }}>{error || 'Unable to load student details.'}</p>
                    <button onClick={fetchStudentData} className="back-link" style={{ margin: '0 auto' }}>Try Again</button>
                </div>
            ) : (
                <>
                    <div className="profile-header-premium shadow-premium">
                        <div className="avatar-massive">
                            {student.name.charAt(0)}
                        </div>
                        <div className="profile-info">
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
                                <div className="status-pill" style={{
                                    background: student.payment_status === 'approved' ? 'color-mix(in srgb, var(--lgl-success) 12%, transparent)' : student.payment_status === 'rejected' ? 'var(--index-danger-bg-soft)' : 'color-mix(in srgb, var(--lgl-warning) 15%, transparent)',
                                    color: student.payment_status === 'approved' ? 'var(--lgl-success)' : student.payment_status === 'rejected' ? 'var(--lgl-error)' : 'var(--lgl-warning)',
                                    borderColor: student.payment_status === 'approved' ? 'color-mix(in srgb, var(--lgl-success) 35%, transparent)' : 'transparent'
                                }}>
                                    <ShieldCheck size={16} /> {student.payment_status === 'approved' ? 'Verified Active Student' : student.payment_status === 'rejected' ? 'Declined / Inactive' : 'Pending Verification'}
                                </div>
                                <div className="status-pill" style={{ background: 'color-mix(in srgb, var(--index-primary-color) 12%, transparent)', color: 'var(--index-primary-color)', borderColor: 'color-mix(in srgb, var(--index-primary-color) 25%, transparent)' }}>
                                    <BookOpen size={16} /> {student.course_name || 'General Program'}
                                </div>
                                <div className="status-pill" style={{ background: 'var(--index-hover-bg)', color: 'var(--index-text-heading)' }}>
                                    <GraduationCap size={16} /> {student.education_level || 'Education Unspecified'}
                                </div>
                                <div className="status-pill" style={{
                                    background: (student.payment_plan === 'installment' || student.payment_plan === '50_percent' || student.payment_plan === '50%')
                                        ? 'color-mix(in srgb, var(--lgl-warning) 15%, transparent)'
                                        : 'color-mix(in srgb, var(--lgl-success) 12%, transparent)',
                                    color: (student.payment_plan === 'installment' || student.payment_plan === '50_percent' || student.payment_plan === '50%')
                                        ? 'var(--lgl-warning)'
                                        : 'var(--lgl-success)',
                                    borderColor: (student.payment_plan === 'installment' || student.payment_plan === '50_percent' || student.payment_plan === '50%')
                                        ? 'color-mix(in srgb, var(--lgl-warning) 35%, transparent)'
                                        : 'color-mix(in srgb, var(--lgl-success) 35%, transparent)'
                                }}>
                                    <Clock size={16} /> {(student.payment_plan === 'installment' || student.payment_plan === '50_percent' || student.payment_plan === '50%') ? '50% Installment (Reminder Active)' : '100% Fully Paid'}
                                </div>
                                <div className="status-pill" style={{
                                    background: student.readiness_completed_at
                                        ? 'color-mix(in srgb, var(--lgl-success) 12%, transparent)'
                                        : 'color-mix(in srgb, var(--index-primary-color) 10%, transparent)',
                                    color: student.readiness_completed_at
                                        ? 'var(--lgl-success)'
                                        : 'var(--index-primary-color)',
                                    borderColor: student.readiness_completed_at
                                        ? 'color-mix(in srgb, var(--lgl-success) 35%, transparent)'
                                        : 'color-mix(in srgb, var(--index-primary-color) 25%, transparent)'
                                }}>
                                    <CheckCircle2 size={16} />
                                    {student.readiness_completed_at
                                        ? 'Day 1 Ready (100% Prepared)'
                                        : `Day 1 Readiness (${student.readiness_checklist ? Object.values(student.readiness_checklist).filter(Boolean).length : 0}/7)`}
                                </div>
                            </div>
                            <h1>{student.name}</h1>
                            <div className="contact-grid-mini">
                                <div className="contact-item-mini"><Mail size={18} /> {student.email}</div>
                                {student.phone && <div className="contact-item-mini"><Phone size={18} /> {student.phone}</div>}
                                <div className="contact-item-mini"><Calendar size={18} /> Member since {new Date(student.created_at).toLocaleDateString()}</div>
                                <div className="contact-item-mini"><MapPin size={18} /> {[student.city, student.state, student.country].filter(Boolean).join(', ') || 'Student Access'}</div>
                            </div>
                        </div>
                        <div style={{ marginLeft: 'auto', alignSelf: 'center' }}>
                            <button
                                onClick={() => navigate(`/instructor/students/${id}/edit`)}
                                className="btn-secondary-outline"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '0.75rem 1.4rem', borderRadius: '16px', fontWeight: 850, fontSize: '0.9rem', background: 'var(--index-card-bg)' }}
                            >
                                <Edit size={16} /> Edit Student
                            </button>
                        </div>
                    </div>

                    <div className="details-layout">
                        <div className="main-records">
                            <div className="card-premium-records shadow-premium">
                                <div className="card-title-records">
                                    <h3><BookOpen size={20} color="var(--index-primary-color)" /> Academic Enrollments</h3>
                                    {student?.payment_status !== 'rejected' && (
                                        <button 
                                            className="btn-secondary-outline" 
                                            onClick={() => {
                                                setShowAssignCohortModal(true);
                                                fetchCohorts();
                                            }}
                                            style={{ display: 'flex', alignItems: 'center', gap: '6px', borderColor: 'var(--lgl-success)', color: 'var(--index-primary-color)', background: 'color-mix(in srgb, var(--lgl-success) 12%, transparent)' }}
                                        >
                                            <Plus size={16} /> Assign Cohort
                                        </button>
                                    )}
                                </div>

                                {student.cohorts && student.cohorts.length > 0 ? student.cohorts.map((cohort: any) => {
                                    const matchingCert = certificates.find((c: any) => c.course_id === cohort.course?.id);
                                    const examSubmission = student.exam_submissions?.find((s: any) => s.cohort_id === cohort.id);
                                    return (
                                        <div key={cohort.id} style={{ marginBottom: '1rem' }}>
                                            <div className="enrollment-row" style={{ marginBottom: 0, borderRadius: expandedCohortMap[cohort.id] ? '20px 20px 0 0' : '20px' }}>
                                                <div className="progress-ring-mini">{Math.round(cohort.pivot?.progress || 0)}%</div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 950, color: 'var(--index-text-heading)', fontSize: '1.1rem' }}>{cohort.name}</div>
                                                    <div style={{ color: 'var(--index-text-secondary)', fontSize: '0.9rem', fontWeight: 600 }}>
                                                        {cohort.course?.title || 'General Curriculum'} • Joined {new Date(cohort.pivot?.created_at).toLocaleDateString()}
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                                                        <div style={{
                                                            fontSize: '0.75rem',
                                                            fontWeight: 850,
                                                            color: cohort.pivot?.status === 'inactive' ? 'var(--lgl-error)' : 'var(--lgl-success)',
                                                            background: cohort.pivot?.status === 'inactive' ? 'var(--index-danger-bg-soft)' : 'color-mix(in srgb, var(--lgl-success) 12%, transparent)',
                                                            padding: '2px 8px',
                                                            borderRadius: '4px'
                                                        }}>
                                                            {cohort.pivot?.status?.toUpperCase() || 'ENROLLED'}
                                                        </div>
                                                        {matchingCert && (
                                                            <div style={{
                                                                fontSize: '0.75rem',
                                                                fontWeight: 850,
                                                                color: 'var(--lgl-cyan-dark)',
                                                                background: 'var(--index-accent-soft-bg)',
                                                                padding: '2px 8px',
                                                                borderRadius: '4px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '4px'
                                                            }}>
                                                                <Award size={12} /> CERTIFICATE ISSUED
                                                            </div>
                                                        )}
                                                        {cohort.exam_enabled && (
                                                            examSubmission ? (
                                                                <div style={{
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 850,
                                                                    color: examSubmission.passed ? 'var(--lgl-success)' : 'var(--lgl-error)',
                                                                    background: examSubmission.passed ? 'color-mix(in srgb, var(--lgl-success) 12%, transparent)' : 'var(--index-danger-bg-soft)',
                                                                    padding: '2px 8px',
                                                                    borderRadius: '4px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px'
                                                                }}>
                                                                    {examSubmission.passed ? <GraduationCap size={12} /> : <XCircle size={12} />}
                                                                    {examSubmission.passed ? 'EXAM PASSED' : 'EXAM FAILED'} ({examSubmission.score}%)
                                                                </div>
                                                            ) : (
                                                                <div style={{
                                                                    fontSize: '0.75rem',
                                                                    fontWeight: 850,
                                                                    color: 'var(--lgl-warning)',
                                                                    background: 'color-mix(in srgb, var(--lgl-warning) 12%, transparent)',
                                                                    padding: '2px 8px',
                                                                    borderRadius: '4px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: '4px'
                                                                }}>
                                                                    <GraduationCap size={12} /> EXAM PENDING
                                                                </div>
                                                            )
                                                        )}
                                                    </div>
                                                </div>
                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    {matchingCert ? (
                                                        <>
                                                            <a 
                                                                href={matchingCert.certificate_path} 
                                                                target="_blank" 
                                                                rel="noreferrer" 
                                                                className="btn-secondary-outline"
                                                                style={{ textDecoration: 'none', background: 'var(--index-accent-soft-bg)', color: 'var(--lgl-cyan-dark)', borderColor: 'color-mix(in srgb, var(--lgl-cyan-dark) 35%, transparent)', display: 'flex', alignItems: 'center', gap: '6px' }}
                                                            >
                                                                View Cert <Award size={14} />
                                                            </a>
                                                            <button
                                                                className="btn-secondary-outline"
                                                                onClick={() => handleOpenReassignModal(cohort, matchingCert)}
                                                                title="Regenerate and reassign this certificate"
                                                                style={{ background: 'color-mix(in srgb, var(--lgl-warning) 15%, transparent)', color: 'var(--lgl-warning)', borderColor: 'color-mix(in srgb, var(--lgl-warning) 35%, transparent)', display: 'flex', alignItems: 'center', gap: '6px' }}
                                                            >
                                                                Reassign <RotateCcw size={14} />
                                                            </button>
                                                        </>
                                                    ) : cohort.course ? (
                                                        <button 
                                                            className="btn-secondary-outline" 
                                                            onClick={() => handleOpenIssueCertModal(cohort)}
                                                            style={{ background: 'color-mix(in srgb, var(--lgl-warning) 15%, transparent)', color: 'var(--lgl-warning)', borderColor: 'color-mix(in srgb, var(--lgl-warning) 35%, transparent)', display: 'flex', alignItems: 'center', gap: '6px' }}
                                                        >
                                                            Issue Cert <Award size={14} />
                                                        </button>
                                                    ) : null}
                                                    <button className="btn-secondary-outline" onClick={() => setExpandedCohortMap(p => ({ ...p, [cohort.id]: !p[cohort.id] }))}>
                                                        {expandedCohortMap[cohort.id] ? 'Hide Progress' : 'Manage Progress'}
                                                    </button>
                                                    <button
                                                        className={cohort.pivot?.status === 'inactive' ? "btn-toggle-active" : "btn-toggle-inactive"}
                                                        onClick={() => toggleActivation(cohort.id, cohort.pivot?.status)}
                                                    >
                                                        {cohort.pivot?.status === 'inactive' ? 'Activate' : 'Deactivate'}
                                                    </button>
                                                </div>
                                            </div>
                                            {expandedCohortMap[cohort.id] && (
                                                <div className="animate-fade-in-up" style={{ padding: '2.5rem', background: 'var(--index-hover-bg)', border: '1.5px solid var(--index-border-subtle)', borderTop: 'none', borderRadius: '0 0 20px 20px', boxShadow: 'inset 0 4px 6px -4px rgba(0,0,0,0.02)' }}>
                                                    <h4 style={{ margin: '0 0 1.5rem 0', color: 'var(--index-text-heading)', fontWeight: 900, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                        <CheckCircle2 size={18} color="var(--index-primary-color)" /> Curriculum Override Access
                                                    </h4>
                                                    <p style={{ margin: '0 0 2rem 0', color: 'var(--index-text-secondary)', fontSize: '0.85rem', fontWeight: 600 }}>Toggle the checkboxes below to manually apply or revoke completion status for a specific resource. This persists immediately to the backend and adjusts percentages automatically.</p>
                                                    
                                                    {cohort.course?.modules?.map((mod: any) => (
                                                        <div key={mod.id} style={{ marginBottom: '1.5rem', background: 'var(--index-card-bg)', border: '1.5px solid var(--index-border-color)', borderRadius: '16px', overflow: 'hidden' }}>
                                                            <div style={{ fontWeight: 800, color: 'var(--index-primary-color)', padding: '1rem 1.5rem', background: 'var(--index-hover-bg)', fontSize: '0.95rem', borderBottom: '1px solid var(--index-border-color)' }}>{mod.title}</div>
                                                            <div style={{ display: 'grid', padding: '1rem' }}>
                                                                {mod.lessons?.map((lesson: any) => {
                                                                    const isCompleted = student?.completed_lessons?.some((cl: any) => cl.id === lesson.id);
                                                                    return (
                                                                        <div key={lesson.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--index-border-subtle)' }}>
                                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                                                                <button 
                                                                                    onClick={() => toggleLessonCompletion(lesson.id, !!isCompleted)}
                                                                                    style={{ width: '26px', height: '26px', borderRadius: '8px', border: `2px solid ${isCompleted ? 'var(--lgl-success)' : 'var(--index-text-faint)'}`, background: isCompleted ? 'var(--lgl-success)' : 'var(--index-card-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0, transition: 'all 0.2s' }}
                                                                                >
                                                                                    {isCompleted && <CheckCircle2 size={16} color="white" />}
                                                                                </button>
                                                                                <span style={{ fontWeight: 700, color: isCompleted ? 'var(--index-text-faint)' : 'var(--index-text-secondary)', fontSize: '0.9rem', textDecoration: isCompleted ? 'line-through' : 'none', transition: 'all 0.2s' }}>{lesson.title}</span>
                                                                            </div>
                                                                            <span style={{ fontSize: '0.7rem', fontWeight: 800, color: 'var(--index-text-secondary)', textTransform: 'uppercase', background: 'var(--index-hover-bg)', padding: '4px 8px', borderRadius: '6px' }}>{lesson.type}</span>
                                                                        </div>
                                                                    );
                                                                })}
                                                                {(!mod.lessons || mod.lessons.length === 0) && (
                                                                    <p style={{ margin: '0.5rem 1rem', color: 'var(--index-text-faint)', fontSize: '0.85rem', fontStyle: 'italic', fontWeight: 600 }}>No lessons active in module...</p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    ))}
                                                    {(!cohort.course?.modules || cohort.course.modules.length === 0) && (
                                                        <p style={{ color: 'var(--index-text-secondary)', fontSize: '0.9rem', fontWeight: 600 }}>No curriculum data bound to this record.</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }) : (
                                    <div style={{ textAlign: 'center', padding: '3rem', background: 'var(--index-hover-bg)', borderRadius: '24px', border: '2px dashed var(--index-border-color)' }}>
                                        <BookOpen size={32} color="var(--index-text-faint)" style={{ marginBottom: '1rem' }} />
                                        <p style={{ color: 'var(--index-text-secondary)', fontWeight: 600 }}>No course enrollments found for this student.</p>
                                    </div>
                                )}
                            </div>

                            <div className="card-premium-records shadow-premium">
                                <div className="card-title-records">
                                    <h3><Award size={20} color="var(--index-primary-color)" /> Learning Metrics</h3>
                                </div>
                                <div className="metrics-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
                                    <div style={{ padding: '1.5rem', background: 'var(--index-hover-bg)', borderRadius: '20px', textAlign: 'center' }}>
                                        <TrendingUp size={24} color="var(--index-primary-color)" style={{ marginBottom: '0.5rem' }} />
                                        <div style={{ color: 'var(--index-text-faint)', fontSize: '0.75rem', fontWeight: 900, textTransform: 'uppercase' }}>Avg. Completion</div>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 950, color: 'var(--index-text-heading)' }}>
                                            {avgCompletion}%
                                        </div>
                                    </div>
                                    <div style={{ padding: '1.5rem', background: 'var(--index-hover-bg)', borderRadius: '20px', textAlign: 'center' }}>
                                        <Clock size={24} color="var(--index-primary-color)" style={{ marginBottom: '0.5rem' }} />
                                        <div style={{ color: 'var(--index-text-faint)', fontSize: '0.75rem', fontWeight: 900, textTransform: 'uppercase' }}>Total Cohorts</div>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 950, color: 'var(--index-text-heading)' }}>{student.cohorts?.length || 0}</div>
                                    </div>
                                    <div style={{ padding: '1.5rem', background: 'var(--index-hover-bg)', borderRadius: '20px', textAlign: 'center' }}>
                                        <Activity size={24} color="var(--index-primary-color)" style={{ marginBottom: '0.5rem' }} />
                                        <div style={{ color: 'var(--index-text-faint)', fontSize: '0.75rem', fontWeight: 900, textTransform: 'uppercase' }}>Avg. Quiz Score</div>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 950, color: 'var(--index-text-heading)' }}>
                                            {avgQuizScore}{avgQuizScore !== 'N/A' ? '%' : ''}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card-premium-records shadow-premium">
                                <div className="card-title-records">
                                    <h3><HelpCircle size={20} color="var(--index-primary-color)" /> Quiz Submissions</h3>
                                </div>

                                 {student.completed_lessons && student.completed_lessons.filter((l: any) => l.type === 'quiz' || (l.quiz_data && l.quiz_data !== 'null' && l.quiz_data !== '{}') || l.pivot?.score != null).length > 0 ? (
                                    <div style={{ display: 'grid', gap: '1rem' }}>
                                        {student.completed_lessons.filter((l: any) => l.type === 'quiz' || (l.quiz_data && l.quiz_data !== 'null' && l.quiz_data !== '{}') || l.pivot?.score != null).map((lesson: any) => (
                                            <div key={lesson.id} className="enrollment-row" style={{ marginBottom: 0 }}>
                                                <div className="progress-ring-mini" style={{ background: (lesson.pivot?.score || 0) >= (lesson.quiz_data?.pass_mark || 80) ? 'color-mix(in srgb, var(--lgl-success) 12%, transparent)' : 'var(--index-danger-bg-soft)', color: (lesson.pivot?.score || 0) >= (lesson.quiz_data?.pass_mark || 80) ? 'var(--index-primary-color)' : 'var(--lgl-error)' }}>
                                                    {lesson.pivot?.score || 0}%
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: 950, color: 'var(--index-text-heading)', fontSize: '1.05rem' }}>{lesson.title}</div>
                                                    <div style={{ color: 'var(--index-text-secondary)', fontSize: '0.85rem', fontWeight: 700 }}>
                                                        Submitted on {new Date(lesson.pivot?.updated_at).toLocaleDateString()}
                                                    </div>
                                                </div>
                                                <button 
                                                    className="btn-secondary-outline" 
                                                    onClick={() => {
                                                        const quizData = typeof lesson.quiz_data === 'string' ? JSON.parse(lesson.quiz_data) : lesson.quiz_data;
                                                        const answers = typeof lesson.pivot.answers === 'string' ? JSON.parse(lesson.pivot.answers) : lesson.pivot.answers;
                                                        setViewingQuizResult({ ...lesson, quiz_data: quizData, pivot: { ...lesson.pivot, answers } });
                                                    }}
                                                >
                                                    Analysis <Activity size={14} />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center', padding: '2rem', background: 'var(--index-hover-bg)', borderRadius: '20px', border: '2px dashed var(--index-border-color)' }}>
                                        <HelpCircle size={24} color="var(--index-text-faint)" style={{ marginBottom: '0.5rem' }} />
                                        <p style={{ color: 'var(--index-text-secondary)', fontWeight: 600, fontSize: '0.9rem' }}>No quiz submissions available yet.</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="sidebar-records">
                            {/* Card 1: Registration & Payment */}
                            <div className="card-premium-records shadow-premium" style={{ padding: '2rem', marginBottom: '2rem' }}>
                                <div className="card-title-records" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3>
                                        <CreditCard size={20} color="var(--index-primary-color)" />
                                        Registration & Payment
                                    </h3>
                                    {updatingStatus && <Loader2 size={18} className="animate-spin" color="var(--index-primary-color)" />}
                                </div>
                                
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                                    {/* Payment Status Badge & Quick Dropdown Switcher */}
                                    <div style={{ background: 'var(--index-hover-bg)', padding: '1rem', borderRadius: '16px', border: '1px solid var(--index-border-subtle)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--index-text-secondary)' }}>Current Status</span>
                                            <span style={{
                                                fontSize: '0.8rem',
                                                fontWeight: 950,
                                                padding: '6px 14px',
                                                borderRadius: '10px',
                                                textTransform: 'uppercase',
                                                letterSpacing: '0.025em',
                                                ...(student.payment_status === 'approved' ? {
                                                    background: 'color-mix(in srgb, var(--lgl-success) 12%, transparent)',
                                                    color: 'var(--index-primary-color)',
                                                    border: '1px solid color-mix(in srgb, var(--lgl-success) 35%, transparent)'
                                                } : student.payment_status === 'rejected' ? {
                                                    background: 'var(--index-danger-bg-soft)',
                                                    color: 'var(--lgl-error)',
                                                    border: '1px solid color-mix(in srgb, var(--lgl-error) 35%, transparent)'
                                                } : {
                                                    background: 'color-mix(in srgb, var(--lgl-warning) 15%, transparent)',
                                                    color: 'var(--lgl-warning)',
                                                    border: '1px solid color-mix(in srgb, var(--lgl-warning) 35%, transparent)'
                                                })
                                            }}>
                                                {student.payment_status === 'approved' ? 'Approved / Active' : student.payment_status === 'rejected' ? 'Rejected' : 'Pending Review'}
                                            </span>
                                        </div>

                                        {/* Status Switcher Dropdown */}
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            <label style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--index-text-faint)' }}>Change Status</label>
                                            <select
                                                value={student.payment_status || 'pending'}
                                                onChange={(e) => handleChangePaymentStatus(e.target.value)}
                                                disabled={updatingStatus}
                                                style={{
                                                    width: '100%',
                                                    padding: '0.65rem 0.85rem',
                                                    borderRadius: '10px',
                                                    border: '1.5px solid var(--index-border-color)',
                                                    background: 'var(--index-card-bg)',
                                                    color: 'var(--index-text-heading)',
                                                    fontWeight: 800,
                                                    fontSize: '0.85rem',
                                                    cursor: updatingStatus ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                <option value="approved">Approved / Verified (Full Access)</option>
                                                <option value="pending">Pending Verification / Review</option>
                                                <option value="rejected">Rejected / Inactive (Lock Access)</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Action Buttons: Quick Approve & Quick Reject */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                                        {student.payment_status !== 'approved' && (
                                            <button
                                                onClick={handleApprovePayment}
                                                disabled={updatingStatus}
                                                style={{
                                                    width: '100%',
                                                    background: 'var(--lgl-success)',
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '0.8rem 1.25rem',
                                                    borderRadius: '14px',
                                                    fontWeight: 850,
                                                    fontSize: '0.88rem',
                                                    cursor: updatingStatus ? 'not-allowed' : 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                    boxShadow: '0 4px 12px color-mix(in srgb, var(--lgl-success) 25%, transparent)',
                                                    transition: 'all 0.2s'
                                                }}
                                            >
                                                {updatingStatus ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={18} />}
                                                <span>Approve & Activate Registration</span>
                                            </button>
                                        )}

                                        {student.payment_status !== 'rejected' && (
                                            <button
                                                onClick={handleRejectPayment}
                                                disabled={updatingStatus}
                                                style={{
                                                    width: '100%',
                                                    background: 'var(--index-danger-bg-soft)',
                                                    color: 'var(--lgl-error)',
                                                    border: '1.5px solid color-mix(in srgb, var(--lgl-error) 25%, transparent)',
                                                    padding: '0.7rem 1.25rem',
                                                    borderRadius: '14px',
                                                    fontWeight: 800,
                                                    fontSize: '0.84rem',
                                                    cursor: updatingStatus ? 'not-allowed' : 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                    transition: 'all 0.2s'
                                                }}
                                            >
                                                {updatingStatus ? <Loader2 size={16} className="animate-spin" /> : <XCircle size={16} />}
                                                <span>Decline / Reject Registration</span>
                                            </button>
                                        )}
                                    </div>

                                    {/* Rejected Warning Notice */}
                                    {student.payment_status === 'rejected' && (
                                        <div style={{
                                            padding: '0.85rem 1rem',
                                            borderRadius: '12px',
                                            background: 'var(--index-danger-bg-soft)',
                                            border: '1px solid color-mix(in srgb, var(--lgl-error) 25%, transparent)',
                                            color: 'var(--lgl-error)',
                                            fontSize: '0.8rem',
                                            fontWeight: 700,
                                            lineHeight: 1.4
                                        }}>
                                            ⚠️ This student's registration is declined. Platform access and cohort enrollments are locked. Click "Approve & Activate Registration" above to restore access.
                                        </div>
                                    )}

                                    {/* Payment Method Badge */}
                                    <div style={{ background: 'var(--index-hover-bg)', padding: '0.85rem 1rem', borderRadius: '14px', border: '1px solid var(--index-border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <div>
                                            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--index-text-faint)', display: 'block', marginBottom: '2px' }}>Payment Method</span>
                                            <span style={{ fontSize: '0.92rem', fontWeight: 850, color: 'var(--index-text-heading)' }}>
                                                {student.payment_method === 'square' ? 'Square Online Checkout' : student.payment_method === 'zelle' ? 'Zelle Transfer' : student.payment_method === 'stripe' ? 'Stripe Gateway' : (student.payment_method || 'Manual')}
                                            </span>
                                        </div>
                                        {student.payment_method === 'square' && (
                                            <span style={{ fontSize: '0.72rem', fontWeight: 800, background: '#006aff', color: 'white', padding: '2px 8px', borderRadius: '6px', textTransform: 'uppercase' }}>
                                                Square Link
                                            </span>
                                        )}
                                        {student.payment_method === 'zelle' && (
                                            <span style={{ fontSize: '0.72rem', fontWeight: 800, background: '#7b3fe4', color: 'white', padding: '2px 8px', borderRadius: '6px', textTransform: 'uppercase' }}>
                                                lglconsultingllc26@gmail.com
                                            </span>
                                        )}
                                    </div>

                                    {/* Payment Receipt Inspection Box */}
                                    <div style={{ background: 'var(--index-hover-bg)', padding: '1.1rem', borderRadius: '16px', border: '1.5px solid var(--index-border-subtle)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--index-text-faint)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                Proof of Payment / Receipt
                                            </span>
                                            {student.receipt_url && (
                                                <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--lgl-success)', background: 'color-mix(in srgb, var(--lgl-success) 12%, transparent)', padding: '2px 8px', borderRadius: '6px' }}>
                                                    ✓ Attached
                                                </span>
                                            )}
                                        </div>

                                        {student.receipt_url ? (
                                            student.receipt_url.toLowerCase().includes('.pdf') ? (
                                                <div style={{
                                                    background: 'var(--index-card-bg)',
                                                    border: '1px solid var(--index-border-color)',
                                                    borderRadius: '12px',
                                                    padding: '1rem',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: '0.75rem'
                                                }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                        <div style={{ width: '38px', height: '38px', borderRadius: '8px', background: 'var(--index-danger-bg-soft)', color: 'var(--lgl-error)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                            <FileText size={20} />
                                                        </div>
                                                        <div style={{ overflow: 'hidden' }}>
                                                            <strong style={{ fontSize: '0.85rem', color: 'var(--index-text-heading)', display: 'block', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                                                PDF Payment Receipt
                                                            </strong>
                                                            <span style={{ fontSize: '0.75rem', color: 'var(--index-text-faint)' }}>Document file</span>
                                                        </div>
                                                    </div>
                                                    <a
                                                        href={student.receipt_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            width: '100%',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '8px',
                                                            padding: '0.65rem 1rem',
                                                            borderRadius: '10px',
                                                            background: 'var(--index-primary-color)',
                                                            color: 'white',
                                                            textDecoration: 'none',
                                                            fontSize: '0.825rem',
                                                            fontWeight: 800,
                                                            boxShadow: '0 2px 6px color-mix(in srgb, var(--index-primary-color) 25%, transparent)',
                                                            transition: 'all 0.2s'
                                                        }}
                                                    >
                                                        <FileText size={15} /> View / Download PDF <ExternalLink size={13} />
                                                    </a>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                                                    <div
                                                        className="receipt-thumbnail-hover"
                                                        onClick={() => setViewingReceiptModal(student.receipt_url)}
                                                        style={{
                                                            position: 'relative',
                                                            borderRadius: '12px',
                                                            overflow: 'hidden',
                                                            border: '1.5px solid var(--index-border-color)',
                                                            cursor: 'pointer',
                                                            maxHeight: '160px',
                                                            background: '#000000'
                                                        }}
                                                    >
                                                        <img
                                                            src={student.receipt_url}
                                                            alt="Payment Receipt"
                                                            style={{
                                                                width: '100%',
                                                                height: '140px',
                                                                objectFit: 'cover',
                                                                display: 'block'
                                                            }}
                                                        />
                                                        <div
                                                            className="receipt-overlay"
                                                            style={{
                                                                position: 'absolute',
                                                                inset: 0,
                                                                background: 'rgba(0,0,0,0.45)',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '6px',
                                                                color: 'white',
                                                                fontWeight: 800,
                                                                fontSize: '0.85rem',
                                                                opacity: 0,
                                                                transition: 'opacity 0.2s'
                                                            }}
                                                        >
                                                            <Eye size={16} /> Click to Enlarge
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                        <button
                                                            type="button"
                                                            onClick={() => setViewingReceiptModal(student.receipt_url)}
                                                            style={{
                                                                flex: 1,
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '6px',
                                                                padding: '0.55rem 0.8rem',
                                                                borderRadius: '8px',
                                                                background: 'var(--index-card-bg)',
                                                                border: '1px solid var(--index-border-color)',
                                                                color: 'var(--index-text-heading)',
                                                                fontSize: '0.8rem',
                                                                fontWeight: 800,
                                                                cursor: 'pointer'
                                                            }}
                                                        >
                                                            <Eye size={14} /> Zoom In
                                                        </button>
                                                        <a
                                                            href={student.receipt_url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '4px',
                                                                padding: '0.55rem 0.8rem',
                                                                borderRadius: '8px',
                                                                background: 'var(--index-card-bg)',
                                                                border: '1px solid var(--index-border-color)',
                                                                color: 'var(--index-text-secondary)',
                                                                textDecoration: 'none',
                                                                fontSize: '0.8rem',
                                                                fontWeight: 750
                                                            }}
                                                        >
                                                            <ExternalLink size={14} /> New Tab
                                                        </a>
                                                    </div>
                                                </div>
                                            )
                                        ) : (
                                            <div style={{
                                                padding: '0.75rem',
                                                borderRadius: '10px',
                                                background: 'var(--index-card-bg)',
                                                border: '1px dashed var(--index-border-color)',
                                                textAlign: 'center',
                                                color: 'var(--index-text-faint)',
                                                fontSize: '0.8rem',
                                                fontWeight: 600
                                            }}>
                                                No uploaded receipt file on record.
                                            </div>
                                        )}
                                    </div>

                                    {/* Official Generated Receipt Actions Box */}
                                    <div style={{
                                        background: 'color-mix(in srgb, var(--index-primary-color) 6%, transparent)',
                                        border: '1.5px solid color-mix(in srgb, var(--index-primary-color) 22%, transparent)',
                                        borderRadius: '16px',
                                        padding: '1.1rem',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '0.75rem'
                                    }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontSize: '0.78rem', fontWeight: 850, color: 'var(--index-primary-color)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <Receipt size={16} /> Official Receipt & Invoice
                                            </span>
                                            <span style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--index-primary-color)', background: 'color-mix(in srgb, var(--index-primary-color) 12%, transparent)', padding: '2px 8px', borderRadius: '6px' }}>
                                                #LGL-{String(student.id || 1).padStart(5, '0')}
                                            </span>
                                        </div>
                                        
                                        <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--index-text-secondary)', lineHeight: 1.45 }}>
                                            Generate or dispatch an official, itemized receipt email with high-resolution PNG attachment to an admin or accounting email.
                                        </p>

                                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                            <button
                                                type="button"
                                                onClick={handleOpenSendReceiptModal}
                                                style={{
                                                    flex: 1,
                                                    minWidth: '160px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px',
                                                    padding: '0.65rem 1rem',
                                                    borderRadius: '10px',
                                                    background: 'var(--index-primary-color)',
                                                    color: '#ffffff',
                                                    border: 'none',
                                                    fontSize: '0.825rem',
                                                    fontWeight: 800,
                                                    cursor: 'pointer',
                                                    boxShadow: '0 2px 8px color-mix(in srgb, var(--index-primary-color) 25%, transparent)',
                                                    transition: 'all 0.2s'
                                                }}
                                            >
                                                <Mail size={15} /> Email Receipt Copy
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setViewingReceiptModal(`${API_URL}/receipts/${student.id}/image`)}
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '6px',
                                                    padding: '0.65rem 0.95rem',
                                                    borderRadius: '10px',
                                                    background: 'var(--index-card-bg)',
                                                    border: '1.5px solid var(--index-border-color)',
                                                    color: 'var(--index-text-heading)',
                                                    fontSize: '0.825rem',
                                                    fontWeight: 800,
                                                    cursor: 'pointer'
                                                }}
                                                title="Preview Generated High-Res Receipt Image"
                                            >
                                                <Eye size={15} /> Preview PNG
                                            </button>
                                        </div>
                                    </div>

                                    {/* Selected Plan */}
                                    <div style={{ background: 'var(--index-hover-bg)', padding: '0.85rem 1rem', borderRadius: '14px', border: '1px solid var(--index-border-subtle)' }}>
                                        <span style={{ fontSize: '0.75rem', fontWeight: 700, color: 'var(--index-text-faint)', display: 'block', marginBottom: '2px' }}>Tuition Plan</span>
                                        <span style={{ fontSize: '0.95rem', fontWeight: 850, color: 'var(--index-text-heading)', textTransform: 'capitalize' }}>
                                            {(student.payment_plan === 'installment' || student.payment_plan === '50_percent' || student.payment_plan === '50%') ? '50% Installment Payment' : '100% Full Payment'}
                                        </span>
                                    </div>

                                    {/* Coupon Code if exists */}
                                    {student.coupon_code && (
                                        <div style={{ background: 'var(--index-hover-bg)', padding: '0.75rem 1rem', borderRadius: '14px', border: '1px solid var(--index-border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontSize: '0.8rem', fontWeight: 750, color: 'var(--index-text-secondary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <Tag size={14} color="var(--index-primary-color)" /> Coupon Applied
                                            </span>
                                            <span style={{ fontSize: '0.85rem', fontWeight: 900, color: 'var(--index-primary-color)', background: 'color-mix(in srgb, var(--index-primary-color) 10%, transparent)', padding: '2px 8px', borderRadius: '6px' }}>
                                                {student.coupon_code}
                                            </span>
                                        </div>
                                    )}

                                    {/* Tuition Reminder State & Quick Switch Box */}
                                    <div style={{
                                        padding: '1.25rem',
                                        borderRadius: '18px',
                                        border: '1.5px solid',
                                        borderColor: (student.payment_plan === 'installment' || student.payment_plan === '50_percent' || student.payment_plan === '50%')
                                            ? 'color-mix(in srgb, var(--lgl-warning) 35%, transparent)'
                                            : 'color-mix(in srgb, var(--lgl-success) 35%, transparent)',
                                        background: (student.payment_plan === 'installment' || student.payment_plan === '50_percent' || student.payment_plan === '50%')
                                            ? 'color-mix(in srgb, var(--lgl-warning) 8%, transparent)'
                                            : 'color-mix(in srgb, var(--lgl-success) 8%, transparent)'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                            {(student.payment_plan === 'installment' || student.payment_plan === '50_percent' || student.payment_plan === '50%') ? (
                                                <>
                                                    <Clock size={18} color="var(--lgl-warning)" />
                                                    <span style={{ fontWeight: 900, fontSize: '0.9rem', color: 'var(--lgl-warning)' }}>50% Installment Reminder Active</span>
                                                </>
                                            ) : (
                                                <>
                                                    <CheckCircle2 size={18} color="var(--lgl-success)" />
                                                    <span style={{ fontWeight: 900, fontSize: '0.9rem', color: 'var(--lgl-success)' }}>100% Fully Paid (No Reminders)</span>
                                                </>
                                            )}
                                        </div>
                                        <p style={{ margin: '0 0 1rem 0', fontSize: '0.8rem', color: 'var(--index-text-secondary)', lineHeight: 1.45, fontWeight: 600 }}>
                                            {(student.payment_plan === 'installment' || student.payment_plan === '50_percent' || student.payment_plan === '50%')
                                                ? "This student paid 50% down. The remaining tuition countdown reminder and balance checkout are currently active on their student dashboard."
                                                : "This student is fully paid. No balance countdown or access locks apply to their account."}
                                        </p>
                                        
                                        {(student.payment_plan === 'installment' || student.payment_plan === '50_percent' || student.payment_plan === '50%') ? (
                                            <button
                                                onClick={() => handleTogglePaymentPlan('full')}
                                                disabled={updatingPlan}
                                                style={{
                                                    width: '100%',
                                                    background: 'var(--lgl-success)',
                                                    color: 'white',
                                                    border: 'none',
                                                    padding: '0.7rem 1rem',
                                                    borderRadius: '12px',
                                                    fontWeight: 850,
                                                    fontSize: '0.825rem',
                                                    cursor: updatingPlan ? 'not-allowed' : 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px'
                                                }}
                                            >
                                                {updatingPlan ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                                                <span>Mark as 100% Fully Paid (Clear Reminder)</span>
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => handleTogglePaymentPlan('installment')}
                                                disabled={updatingPlan}
                                                style={{
                                                    width: '100%',
                                                    background: 'transparent',
                                                    color: 'var(--lgl-warning)',
                                                    border: '1.5px solid var(--lgl-warning)',
                                                    padding: '0.7rem 1rem',
                                                    borderRadius: '12px',
                                                    fontWeight: 850,
                                                    fontSize: '0.825rem',
                                                    cursor: updatingPlan ? 'not-allowed' : 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px'
                                                }}
                                            >
                                                {updatingPlan ? <Loader2 size={16} className="animate-spin" /> : <Clock size={16} />}
                                                <span>Switch to 50% Installment Plan</span>
                                            </button>
                                        )}
                                    </div>

                                    {/* 50% Balance Payment & Expiration Tracker Box */}
                                    {(() => {
                                        const hasCustomOverrides = Boolean(student.custom_payment_link || student.custom_payment_amount || student.payment_deadline);
                                        return (
                                            <div style={{
                                                background: hasCustomOverrides ? 'color-mix(in srgb, var(--index-primary-color) 8%, transparent)' : 'var(--index-hover-bg)',
                                                border: `1.5px solid ${hasCustomOverrides ? 'color-mix(in srgb, var(--index-primary-color) 30%, transparent)' : 'var(--index-border-subtle)'}`,
                                                borderRadius: '16px',
                                                padding: '1.25rem',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '0.75rem'
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <span style={{ fontSize: '0.78rem', fontWeight: 850, color: 'var(--index-text-heading)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        <ExternalLink size={16} color="var(--index-primary-color)" /> Payment Tracker & Expiration Overrides
                                                    </span>
                                                    <span style={{
                                                        fontSize: '0.7rem',
                                                        fontWeight: 900,
                                                        padding: '2px 8px',
                                                        borderRadius: '6px',
                                                        textTransform: 'uppercase',
                                                        background: hasCustomOverrides
                                                            ? 'color-mix(in srgb, var(--index-primary-color) 15%, transparent)'
                                                            : 'color-mix(in srgb, var(--lgl-success) 12%, transparent)',
                                                        color: hasCustomOverrides ? 'var(--index-primary-color)' : 'var(--lgl-success)'
                                                    }}>
                                                        {hasCustomOverrides ? `★ Custom Overrides Active` : '⚡ Automatic Default'}
                                                    </span>
                                                </div>

                                                <p style={{ margin: 0, fontSize: '0.78rem', color: 'var(--index-text-secondary)', lineHeight: 1.45 }}>
                                                    {hasCustomOverrides
                                                        ? `Custom payment tracker configuration active for this student. ${student.custom_payment_amount ? `50% Balance: $${student.custom_payment_amount}. ` : ''}${student.payment_deadline ? `Custom Expiration: ${formatCSTDateDisplay(student.payment_deadline)}. ` : ''}Student's dashboard tracker reflects these settings.`
                                                        : `Using standard course default expiration & pricing (${(student.course_name?.toLowerCase().includes('bundle') || student.course_name?.toLowerCase().includes('all-in-one') || student.course_name?.toLowerCase().includes('cohort 12') || student.course_name?.toLowerCase().includes('cohort-12') || student.course_name?.toLowerCase().includes('cohort12') || student.course_name?.toLowerCase().includes('12')) ? '$900 Dual Bundle Link' : '$600 Standard Course Link'}).`}
                                                </p>

                                                {/* Custom Expiration Badge if configured */}
                                                {student.payment_deadline && (
                                                    <div style={{
                                                        background: 'var(--index-card-bg)',
                                                        border: '1px solid color-mix(in srgb, var(--index-primary-color) 25%, transparent)',
                                                        borderRadius: '10px',
                                                        padding: '0.6rem 0.85rem',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '8px'
                                                    }}>
                                                        <Calendar size={16} color="var(--index-primary-color)" />
                                                        <div style={{ fontSize: '0.78rem', color: 'var(--index-text-heading)', fontWeight: 700 }}>
                                                            <span style={{ color: 'var(--index-primary-color)', fontWeight: 850 }}>Custom Expiration: </span>
                                                            {formatCSTDateDisplay(student.payment_deadline)}
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Link preview input */}
                                                <div style={{
                                                    background: 'var(--index-card-bg)',
                                                    border: '1px solid var(--index-border-color)',
                                                    borderRadius: '10px',
                                                    padding: '0.6rem 0.85rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    gap: '8px'
                                                }}>
                                                    <div style={{ minWidth: 0, flex: 1 }}>
                                                        <div style={{
                                                            fontSize: '0.78rem',
                                                            color: 'var(--index-text-heading)',
                                                            fontWeight: 700,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            fontFamily: 'monospace'
                                                        }}>
                                                            {student.custom_payment_link || ((student.course_name?.toLowerCase().includes('bundle') || student.course_name?.toLowerCase().includes('all-in-one') || student.course_name?.toLowerCase().includes('cohort 12') || student.course_name?.toLowerCase().includes('cohort-12') || student.course_name?.toLowerCase().includes('cohort12') || student.course_name?.toLowerCase().includes('12')) ? 'https://square.link/u/em8IM5hW' : 'https://square.link/u/HjJ7Mn7U')}
                                                        </div>
                                                        {student.custom_payment_amount && (
                                                            <div style={{ fontSize: '0.72rem', fontWeight: 800, color: 'var(--index-primary-color)', marginTop: '2px' }}>
                                                                Tracker 50% Amount: ${student.custom_payment_amount} USD
                                                            </div>
                                                        )}
                                                    </div>
                                                    <a
                                                        href={student.custom_payment_link || ((student.course_name?.toLowerCase().includes('bundle') || student.course_name?.toLowerCase().includes('all-in-one') || student.course_name?.toLowerCase().includes('cohort 12') || student.course_name?.toLowerCase().includes('cohort-12') || student.course_name?.toLowerCase().includes('cohort12') || student.course_name?.toLowerCase().includes('12')) ? 'https://square.link/u/em8IM5hW' : 'https://square.link/u/HjJ7Mn7U')}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        title="Open link in new tab"
                                                        style={{ color: 'var(--index-primary-color)', display: 'flex', alignItems: 'center', flexShrink: 0 }}
                                                    >
                                                        <ExternalLink size={15} />
                                                    </a>
                                                </div>

                                                {/* Buttons: Edit Custom Link & Reset */}
                                                <div style={{ display: 'flex', gap: '8px', marginTop: '2px' }}>
                                                    <button
                                                        type="button"
                                                        onClick={handleEditCustomPaymentLink}
                                                        disabled={updatingPlan}
                                                        style={{
                                                            flex: 1,
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '6px',
                                                            padding: '0.55rem 0.85rem',
                                                            borderRadius: '10px',
                                                            background: 'var(--index-primary-color)',
                                                            color: '#ffffff',
                                                            border: 'none',
                                                            fontSize: '0.8rem',
                                                            fontWeight: 850,
                                                            cursor: updatingPlan ? 'not-allowed' : 'pointer',
                                                            transition: 'all 0.2s'
                                                        }}
                                                    >
                                                        <Edit size={14} /> {hasCustomOverrides ? 'Edit Custom Details & Expiration' : 'Set Custom Expiration & Details'}
                                                    </button>

                                                    {hasCustomOverrides && (
                                                        <button
                                                            type="button"
                                                            onClick={handleClearCustomPaymentLink}
                                                            disabled={updatingPlan}
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '4px',
                                                                padding: '0.55rem 0.85rem',
                                                                borderRadius: '10px',
                                                                background: 'var(--index-card-bg)',
                                                                border: '1px solid var(--index-border-color)',
                                                                color: 'var(--lgl-error)',
                                                                fontSize: '0.8rem',
                                                                fontWeight: 750,
                                                                cursor: updatingPlan ? 'not-allowed' : 'pointer'
                                                            }}
                                                            title="Revert back to automatic flow"
                                                        >
                                                            <RotateCcw size={13} /> Reset
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })()}

                                    {/* 2FA Security & Exemption Card */}
                                    <div style={{
                                        background: student.two_factor_exempt 
                                            ? 'color-mix(in srgb, var(--lgl-warning) 10%, transparent)' 
                                            : (student.two_factor_enabled ? 'color-mix(in srgb, var(--lgl-success) 8%, transparent)' : 'var(--index-hover-bg)'),
                                        border: `1.5px solid ${student.two_factor_exempt 
                                            ? 'color-mix(in srgb, var(--lgl-warning) 30%, transparent)' 
                                            : (student.two_factor_enabled ? 'color-mix(in srgb, var(--lgl-success) 25%, transparent)' : 'var(--index-border-color)')}`,
                                        borderRadius: '16px',
                                        padding: '1.25rem',
                                        marginBottom: '1rem'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.65rem' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 850, fontSize: '0.9rem', color: 'var(--index-text-heading)' }}>
                                                {student.two_factor_exempt ? <ShieldAlert size={18} color="var(--lgl-warning)" /> : <ShieldCheck size={18} color={student.two_factor_enabled ? 'var(--lgl-success)' : 'var(--index-text-secondary)'} />}
                                                <span>Two-Factor Authentication (2FA)</span>
                                            </div>
                                            <span style={{
                                                fontSize: '0.72rem',
                                                fontWeight: 900,
                                                padding: '2px 8px',
                                                borderRadius: '6px',
                                                textTransform: 'uppercase',
                                                background: student.two_factor_exempt 
                                                    ? 'color-mix(in srgb, var(--lgl-warning) 20%, transparent)' 
                                                    : (student.two_factor_enabled ? 'color-mix(in srgb, var(--lgl-success) 20%, transparent)' : 'var(--index-border-color)'),
                                                color: student.two_factor_exempt 
                                                    ? 'var(--lgl-warning)' 
                                                    : (student.two_factor_enabled ? 'var(--lgl-success)' : 'var(--index-text-secondary)')
                                            }}>
                                                {student.two_factor_exempt ? 'Exempted' : (student.two_factor_enabled ? 'Active' : 'Mandatory')}
                                            </span>
                                        </div>

                                        <p style={{ fontSize: '0.8rem', color: 'var(--index-text-secondary)', fontWeight: 600, margin: '0 0 1rem 0', lineHeight: 1.45 }}>
                                            {student.two_factor_exempt
                                                ? 'This student has been granted a special exemption to log in without requiring 2FA.'
                                                : (student.two_factor_enabled
                                                    ? '2FA is configured and active for this student account.'
                                                    : 'Mandatory 2FA is enforced. The student must verify 2FA upon login unless exempted.')}
                                        </p>

                                        <button
                                            onClick={handleToggle2faExemption}
                                            disabled={updating2faExempt}
                                            style={{
                                                width: '100%',
                                                background: student.two_factor_exempt ? 'var(--index-card-bg)' : 'transparent',
                                                color: student.two_factor_exempt ? 'var(--lgl-error)' : 'var(--index-primary-color)',
                                                border: `1.5px solid ${student.two_factor_exempt ? 'var(--lgl-error)' : 'var(--index-primary-color)'}`,
                                                padding: '0.65rem 1rem',
                                                borderRadius: '12px',
                                                fontWeight: 850,
                                                fontSize: '0.825rem',
                                                cursor: updating2faExempt ? 'not-allowed' : 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '8px'
                                            }}
                                        >
                                            {updating2faExempt ? <Loader2 size={15} className="animate-spin" /> : (student.two_factor_exempt ? <ShieldCheck size={15} /> : <ShieldAlert size={15} />)}
                                            <span>{student.two_factor_exempt ? 'Revoke Exemption (Enforce 2FA)' : 'Grant Special 2FA Exemption'}</span>
                                        </button>
                                    </div>

                                    {/* Send Credentials Button */}
                                    <button
                                        onClick={handleSendCredentials}
                                        disabled={sendingCredentials}
                                        style={{
                                            width: '100%',
                                            background: 'var(--index-hover-bg)',
                                            color: 'var(--index-text-heading)',
                                            border: '1.5px solid var(--index-border-color)',
                                            padding: '0.85rem 1.25rem',
                                            borderRadius: '16px',
                                            fontWeight: 850,
                                            fontSize: '0.88rem',
                                            cursor: sendingCredentials ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {sendingCredentials ? (
                                            <>
                                                <Loader2 className="animate-spin" size={16} />
                                                <span>Dispatching Credentials...</span>
                                            </>
                                        ) : (
                                            <>
                                                <Key size={16} color="var(--index-primary-color)" />
                                                <span>Send / Resend Login Credentials</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {/* Card: Software License Keys (Day 1 Readiness) */}
                            <div className="card-premium-records shadow-premium" style={{ padding: '2rem', marginBottom: '2rem' }}>
                                <div className="card-title-records" style={{ marginBottom: '1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3 style={{ fontSize: '1.1rem' }}>
                                        <Key size={20} color="var(--index-primary-color)" />
                                        Microsoft Project License Key
                                    </h3>
                                    {savingKeys && <Loader2 size={18} className="animate-spin" color="var(--index-primary-color)" />}
                                </div>

                                <p style={{ fontSize: '0.8rem', color: 'var(--index-text-secondary)', fontWeight: 600, margin: '0 0 1.25rem 0', lineHeight: 1.45 }}>
                                    Assign the student's Microsoft Project (MSP) product activation key. The student can view and 1-click copy this key directly on their Day 1 Readiness page.
                                </p>

                                {student.readiness_completed_at ? (
                                    <div style={{ padding: '0.75rem 1rem', background: 'color-mix(in srgb, var(--lgl-success) 10%, transparent)', borderRadius: '14px', border: '1px solid color-mix(in srgb, var(--lgl-success) 25%, transparent)', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.8rem', fontWeight: 800, color: 'var(--lgl-success)', marginBottom: '1.25rem' }}>
                                        <CheckCircle2 size={16} />
                                        <span>Student completed all 7 Day 1 Readiness steps!</span>
                                    </div>
                                ) : (
                                    <div style={{ padding: '0.75rem 1rem', background: 'var(--index-hover-bg)', borderRadius: '14px', border: '1px solid var(--index-border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.8rem', fontWeight: 700, color: 'var(--index-text-secondary)', marginBottom: '1.25rem' }}>
                                        <span>Student Day 1 Progress:</span>
                                        <span style={{ fontWeight: 900, color: 'var(--index-primary-color)' }}>
                                            {student.readiness_checklist ? Object.values(student.readiness_checklist).filter(Boolean).length : 0} / 7 Steps Done
                                        </span>
                                    </div>
                                )}

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {/* Microsoft Project (MSP) License Key */}
                                    <div style={{ background: 'var(--index-hover-bg)', padding: '1rem', borderRadius: '16px', border: '1px solid var(--index-border-subtle)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <label style={{ fontSize: '0.78rem', fontWeight: 800, color: 'var(--index-text-heading)' }}>
                                                Microsoft Project (MSP) Key
                                            </label>
                                            <span style={{
                                                fontSize: '0.7rem',
                                                fontWeight: 900,
                                                padding: '2px 8px',
                                                borderRadius: '8px',
                                                textTransform: 'uppercase',
                                                ...(mspKey ? {
                                                    background: 'color-mix(in srgb, var(--lgl-success) 12%, transparent)',
                                                    color: 'var(--lgl-success)'
                                                } : {
                                                    background: 'var(--index-hover-bg)',
                                                    color: 'var(--index-text-faint)'
                                                })
                                            }}>
                                                {mspKey ? 'Assigned' : 'Unassigned'}
                                            </span>
                                        </div>
                                        <input
                                            type="text"
                                            placeholder="e.g. MSP-PRO-2026-ZZZZ-WWWW"
                                            value={mspKey}
                                            onChange={(e) => setMspKey(e.target.value)}
                                            style={{
                                                width: '100%',
                                                padding: '0.65rem 0.85rem',
                                                borderRadius: '10px',
                                                border: '1.5px solid var(--index-border-color)',
                                                background: 'var(--index-card-bg)',
                                                color: 'var(--index-text-heading)',
                                                fontWeight: 700,
                                                fontSize: '0.85rem',
                                                fontFamily: 'monospace'
                                            }}
                                        />
                                    </div>

                                    {/* Save License Keys Button */}
                                    <button
                                        onClick={handleSaveLicenseKeys}
                                        disabled={savingKeys}
                                        style={{
                                            width: '100%',
                                            background: 'var(--index-primary-color)',
                                            color: 'white',
                                            border: 'none',
                                            padding: '0.8rem 1.25rem',
                                            borderRadius: '14px',
                                            fontWeight: 850,
                                            fontSize: '0.88rem',
                                            cursor: savingKeys ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '8px',
                                            boxShadow: '0 4px 12px color-mix(in srgb, var(--index-primary-color) 25%, transparent)',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        {savingKeys ? <Loader2 size={16} className="animate-spin" /> : <Key size={16} />}
                                        <span>Save MSP License Key</span>
                                    </button>
                                </div>
                            </div>

                            {/* Card 2: Academic & Student Profile Details */}
                            <div className="card-premium-records shadow-premium" style={{ padding: '2rem', marginBottom: '2rem' }}>
                                <div className="card-title-records" style={{ marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <h3>
                                        <Info size={20} color="var(--index-primary-color)" />
                                        Academic & Profile Details
                                    </h3>
                                    <button 
                                        onClick={() => navigate(`/instructor/students/${id}/edit`)}
                                        style={{ background: 'none', border: 'none', color: 'var(--index-primary-color)', cursor: 'pointer', fontWeight: 800, fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '4px' }}
                                    >
                                        <Edit size={14} /> Edit
                                    </button>
                                </div>
                                
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
                                    {/* Program Track */}
                                    <div style={{ background: 'var(--index-hover-bg)', padding: '0.85rem 1rem', borderRadius: '14px', border: '1px solid var(--index-border-subtle)' }}>
                                        <span style={{ fontSize: '0.725rem', fontWeight: 800, color: 'var(--index-text-faint)', display: 'block', textTransform: 'uppercase', marginBottom: '2px' }}>Course / Program Track</span>
                                        <span style={{ fontSize: '0.95rem', fontWeight: 850, color: 'var(--index-text-heading)' }}>
                                            {student.course_name || 'Foundation Academy'}
                                        </span>
                                    </div>

                                    {/* Education Level */}
                                    <div style={{ background: 'var(--index-hover-bg)', padding: '0.85rem 1rem', borderRadius: '14px', border: '1px solid var(--index-border-subtle)' }}>
                                        <span style={{ fontSize: '0.725rem', fontWeight: 800, color: 'var(--index-text-faint)', display: 'block', textTransform: 'uppercase', marginBottom: '2px' }}>Education Level</span>
                                        <span style={{ fontSize: '0.95rem', fontWeight: 850, color: 'var(--index-text-heading)' }}>
                                            {student.education_level || 'Not provided'}
                                        </span>
                                    </div>

                                    {/* Phone Number */}
                                    {student.phone && (
                                        <div style={{ background: 'var(--index-hover-bg)', padding: '0.85rem 1rem', borderRadius: '14px', border: '1px solid var(--index-border-subtle)' }}>
                                            <span style={{ fontSize: '0.725rem', fontWeight: 800, color: 'var(--index-text-faint)', display: 'block', textTransform: 'uppercase', marginBottom: '2px' }}>Telephone</span>
                                            <span style={{ fontSize: '0.95rem', fontWeight: 850, color: 'var(--index-text-heading)' }}>
                                                {student.phone}
                                            </span>
                                        </div>
                                    )}

                                    {/* Address Details */}
                                    {(student.address_line1 || student.city || student.country) && (
                                        <div style={{ background: 'var(--index-hover-bg)', padding: '0.85rem 1rem', borderRadius: '14px', border: '1px solid var(--index-border-subtle)' }}>
                                            <span style={{ fontSize: '0.725rem', fontWeight: 800, color: 'var(--index-text-faint)', display: 'block', textTransform: 'uppercase', marginBottom: '4px' }}>Residential Address</span>
                                            <span style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--index-text-heading)', lineHeight: 1.45, display: 'block' }}>
                                                {[student.address_line1, student.address_line2, student.city, student.state, student.zip, student.country].filter(Boolean).join(', ')}
                                            </span>
                                        </div>
                                    )}

                                    {/* How Heard */}
                                    <div style={{ background: 'var(--index-hover-bg)', padding: '0.85rem 1rem', borderRadius: '14px', border: '1px solid var(--index-border-subtle)' }}>
                                        <span style={{ fontSize: '0.725rem', fontWeight: 800, color: 'var(--index-text-faint)', display: 'block', textTransform: 'uppercase', marginBottom: '2px' }}>Discovery Channel</span>
                                        <span style={{ fontSize: '0.95rem', fontWeight: 850, color: 'var(--index-text-heading)', textTransform: 'capitalize' }}>
                                            {student.hear_source ? (student.hear_source === 'referral' ? 'Friend / Referral' : student.hear_source) : 'Not specified'}
                                        </span>
                                    </div>

                                    {/* Referral Information Card */}
                                    {(student.referral_name || student.referral_email || student.hear_source === 'referral') && (
                                        <div style={{ background: 'color-mix(in srgb, var(--index-primary-color) 4%, transparent)', padding: '1rem', borderRadius: '16px', border: '1.5px dashed color-mix(in srgb, var(--index-primary-color) 25%, transparent)' }}>
                                            <span style={{ fontSize: '0.725rem', fontWeight: 900, color: 'var(--index-primary-color)', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '0.6rem' }}>
                                                Referral Details
                                            </span>
                                            
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                                <div>
                                                    <span style={{ fontSize: '0.725rem', fontWeight: 700, color: 'var(--index-text-secondary)', display: 'block' }}>Referred by:</span>
                                                    <span style={{ fontSize: '0.9rem', fontWeight: 850, color: 'var(--index-text-heading)' }}>
                                                        {student.referral_name || 'N/A'}
                                                    </span>
                                                </div>
                                                <div>
                                                    <span style={{ fontSize: '0.725rem', fontWeight: 700, color: 'var(--index-text-secondary)', display: 'block' }}>Referee Email:</span>
                                                    {student.referral_email ? (
                                                        <a 
                                                            href={`mailto:${student.referral_email}`}
                                                            style={{ fontSize: '0.9rem', fontWeight: 850, color: 'var(--lgl-cyan-dark)', textDecoration: 'none' }}
                                                        >
                                                            {student.referral_email}
                                                        </a>
                                                    ) : (
                                                        <span style={{ fontSize: '0.9rem', fontWeight: 850, color: 'var(--index-text-heading)' }}>N/A</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Terms & Security Info */}
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginTop: '0.25rem' }}>
                                        <div style={{ background: 'var(--index-hover-bg)', padding: '0.65rem 0.85rem', borderRadius: '12px', border: '1px solid var(--index-border-subtle)' }}>
                                            <span style={{ fontSize: '0.7rem', fontWeight: 800, color: 'var(--index-text-faint)', display: 'block' }}>Terms Policy</span>
                                            <span style={{ fontSize: '0.8rem', fontWeight: 850, color: student.agree_terms ? 'var(--lgl-success)' : 'var(--index-text-secondary)' }}>
                                                {student.agree_terms ? '✓ Agreed' : 'Standard'}
                                            </span>
                                        </div>
                                        <div style={{ background: 'var(--index-hover-bg)', padding: '0.65rem 0.85rem', borderRadius: '12px', border: '1px solid var(--index-border-subtle)' }}>
                                            <span style={{ fontSize: '0.7rem', fontWeight: 800, color: 'var(--index-text-faint)', display: 'block' }}>Two-Factor (2FA)</span>
                                            <span style={{ fontSize: '0.8rem', fontWeight: 850, color: student.two_factor_enabled ? 'var(--lgl-success)' : 'var(--index-text-secondary)' }}>
                                                {student.two_factor_enabled ? '✓ Enabled' : 'Disabled'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="card-premium-records shadow-premium" style={{ padding: '2rem' }}>
                                <div className="card-title-records">
                                    <h3><Activity size={20} color="var(--index-primary-color)" /> History Log</h3>
                                </div>
                                <div style={{ marginTop: '1.5rem' }}>
                                    <div className="activity-item">
                                        <div className="activity-content">
                                            <div>Account Activated</div>
                                            <span>{new Date(student.created_at).toLocaleString()}</span>
                                        </div>
                                    </div>
                                    {student.cohorts?.slice(0, 3).map((c: any) => (
                                        <div key={c.id} className="activity-item">
                                            <div className="activity-content">
                                                <div>Joined {c.name}</div>
                                                <span>{new Date(c.pivot?.created_at).toLocaleString()}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {showDeactivateModal && (
                <div className="modal-overlay">
                    <div className="modal-box animate-fade-in-up">
                        <h3>Deactivate Access</h3>
                        <p style={{ color: 'var(--index-text-secondary)', fontWeight: 600, marginBottom: '1.5rem', fontSize: '0.9rem' }}>
                            You are about to deactivate access for <strong>{student?.name}</strong>. Input a message explaining why (this will be sent to their email).
                        </p>
                        <textarea
                            placeholder="e.g. Your subscription has expired or you've completed the program curriculum..."
                            value={deactivateMessage}
                            onChange={(e) => setDeactivateMessage(e.target.value)}
                        />
                        <div className="modal-actions">
                            <button className="btn-cancel" onClick={() => {
                                setShowDeactivateModal(false);
                                setPendingCohortId(null);
                                setDeactivateMessage('');
                            }}>Cancel</button>
                            <button className="btn-confirm" onClick={confirmDeactivation}>Deactivate Access</button>
                        </div>
                    </div>
                </div>
            )}

            {viewingQuizResult && (
                <div className="modal-overlay" style={{ zIndex: 1100 }}>
                    <div className="modal-box animate-fade-in-up" style={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', padding: 0 }}>
                        <div style={{ padding: '2rem 2.5rem', borderBottom: '1px solid var(--index-border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--index-hover-bg)', borderTopLeftRadius: '28px', borderTopRightRadius: '28px' }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.4rem' }}>Evaluation Intelligence Analysis</h3>
                                <p style={{ margin: '4px 0 0 0', color: 'var(--index-text-secondary)', fontWeight: 700, fontSize: '0.9rem' }}>
                                    {viewingQuizResult.title} • Score: <span style={{ color: viewingQuizResult.pivot.score >= (viewingQuizResult.quiz_data?.pass_mark || 80) ? 'var(--lgl-success)' : 'var(--lgl-error)' }}>{viewingQuizResult.pivot.score}%</span>
                                </p>
                            </div>
                            <button onClick={() => setViewingQuizResult(null)} style={{ background: 'var(--index-card-bg)', border: '1.5px solid var(--index-border-color)', width: '40px', height: '40px', borderRadius: '12px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <X size={20} />
                            </button>
                        </div>
                        
                        <div style={{ flex: 1, overflowY: 'auto', padding: '2.5rem' }}>
                            {viewingQuizResult.quiz_data?.questions?.map((q: any, idx: number) => {
                                const studentAnswer = viewingQuizResult.pivot.answers[idx];
                                const rawCorrect = q.correct_answer !== undefined ? q.correct_answer : (q.correctAnswer !== undefined ? q.correctAnswer : q.correct_option);
                                const rightAns = typeof rawCorrect === 'string' && rawCorrect.length === 1 && rawCorrect >= 'A' && rawCorrect <= 'Z'
                                    ? rawCorrect.charCodeAt(0) - 65
                                    : Number(rawCorrect);
                                const isCorrect = studentAnswer !== undefined && Number(studentAnswer) === rightAns;
                                
                                return (
                                    <div key={idx} style={{ marginBottom: '2rem', padding: '1.5rem', borderRadius: '20px', border: `1.5px solid ${isCorrect ? 'color-mix(in srgb, var(--lgl-success) 12%, transparent)' : 'var(--index-danger-bg-soft)'}`, background: isCorrect ? 'var(--index-hover-bg)' : 'var(--index-danger-bg-soft)' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                                            <span style={{ fontSize: '0.75rem', fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--index-text-faint)' }}>Question {idx + 1}</span>
                                            <span style={{ fontSize: '0.75rem', fontWeight: 900, color: isCorrect ? 'var(--lgl-success)' : 'var(--lgl-error)', background: isCorrect ? 'color-mix(in srgb, var(--lgl-success) 12%, transparent)' : 'var(--index-danger-bg-soft)', padding: '4px 10px', borderRadius: '8px' }}>
                                                {isCorrect ? 'VALIDATED' : 'ERRONEOUS'}
                                            </span>
                                        </div>
                                        <h4 style={{ margin: '0 0 1.5rem 0', fontWeight: 850, color: 'var(--index-text-heading)', lineHeight: 1.4 }}>{q.question}</h4>
                                        <div style={{ display: 'grid', gap: '0.75rem' }}>
                                            {q.options.map((opt: string, oIdx: number) => {
                                                const isStudentPick = studentAnswer !== undefined && Number(studentAnswer) === oIdx;
                                                const isRightAnswer = oIdx === rightAns;
                                                
                                                return (
                                                    <div 
                                                        key={oIdx} 
                                                        style={{ 
                                                            padding: '1rem', 
                                                            borderRadius: '12px', 
                                                            background: isRightAnswer ? 'color-mix(in srgb, var(--lgl-success) 12%, transparent)' : isStudentPick ? 'var(--index-danger-bg-soft)' : 'var(--index-card-bg)',
                                                            border: `1.2px solid ${isRightAnswer ? 'color-mix(in srgb, var(--lgl-success) 25%, transparent)' : isStudentPick ? 'color-mix(in srgb, var(--lgl-error) 25%, transparent)' : 'var(--index-hover-bg)'}`,
                                                            color: isRightAnswer ? 'var(--lgl-success)' : isStudentPick ? 'var(--lgl-error)' : 'var(--index-text-secondary)',
                                                            fontWeight: (isStudentPick || isRightAnswer) ? 800 : 500,
                                                            fontSize: '0.9rem',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '12px'
                                                        }}
                                                    >
                                                        <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: '2px solid currentColor', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                                            {(isStudentPick || isRightAnswer) && <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'currentColor' }}></div>}
                                                        </div>
                                                        {opt}
                                                        {isRightAnswer && <CheckCircle2 size={16} style={{ marginLeft: 'auto' }} />}
                                                        {!isCorrect && isStudentPick && <X size={16} style={{ marginLeft: 'auto' }} />}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        
                        <div style={{ padding: '1.5rem 2.5rem', borderTop: '1px solid var(--index-border-subtle)', background: 'var(--index-hover-bg)', display: 'flex', justifyContent: 'flex-end', borderBottomLeftRadius: '28px', borderBottomRightRadius: '28px' }}>
                            <button onClick={() => setViewingQuizResult(null)} className="btn-confirm" style={{ background: 'var(--index-text-heading)' }}>Close Analysis</button>
                        </div>
                    </div>
                </div>
            )}

            {showAssignCohortModal && (
                <div className="modal-overlay" style={{ zIndex: 1150 }}>
                    <div className="modal-box animate-fade-in-up" style={{ maxWidth: '500px', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{
                                    width: '42px',
                                    height: '42px',
                                    borderRadius: '12px',
                                    background: 'color-mix(in srgb, var(--lgl-success) 12%, transparent)',
                                    color: 'var(--index-primary-color)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1.5px solid color-mix(in srgb, var(--lgl-success) 15%, transparent)'
                                }}>
                                    <Layers size={20} />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 950, color: 'var(--index-text-heading)' }}>
                                        Assign Academic Cohort
                                    </h3>
                                    <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--index-text-secondary)', fontWeight: 600 }}>
                                        Enroll {student.name} in standard learning programs
                                    </p>
                                </div>
                            </div>
                            <button 
                                onClick={() => {
                                    setShowAssignCohortModal(false);
                                    setSelectedCohortIds([]);
                                }} 
                                style={{ background: 'var(--index-hover-bg)', border: '1.5px solid var(--index-border-color)', width: '36px', height: '36px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {loadingCohorts ? (
                            <div style={{ padding: '3rem 0', textAlign: 'center' }}>
                                <Loader2 className="animate-spin" size={32} color="var(--index-primary-color)" style={{ margin: '0 auto' }} />
                                <p style={{ marginTop: '1rem', fontWeight: 800, color: 'var(--index-text-secondary)', fontSize: '0.85rem' }}>Loading active cohorts...</p>
                            </div>
                        ) : (
                            <>
                                <div style={{ maxHeight: '300px', overflowY: 'auto', paddingRight: '4px', marginBottom: '1.5rem' }}>
                                    {allCohorts.filter((c: any) => !(student.cohorts || []).some((sc: any) => sc.id === c.id)).length > 0 ? (
                                        allCohorts.filter((c: any) => !(student.cohorts || []).some((sc: any) => sc.id === c.id)).map((cohort: any) => {
                                            const isSelected = selectedCohortIds.includes(cohort.id);
                                            return (
                                                <div 
                                                    key={cohort.id}
                                                    onClick={() => {
                                                        setSelectedCohortIds(prev => 
                                                            isSelected ? prev.filter(id => id !== cohort.id) : [...prev, cohort.id]
                                                        );
                                                    }}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                        padding: '12px 16px',
                                                        background: isSelected ? 'color-mix(in srgb, var(--lgl-success) 12%, transparent)' : 'var(--index-card-bg)',
                                                        border: `1.5px solid ${isSelected ? 'var(--lgl-success)' : 'var(--index-hover-bg)'}`,
                                                        borderRadius: '16px',
                                                        marginBottom: '8px',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s'
                                                    }}
                                                >
                                                    <div style={{
                                                        width: '20px',
                                                        height: '20px',
                                                        borderRadius: '6px',
                                                        border: `2px solid ${isSelected ? 'var(--lgl-success)' : 'var(--index-text-faint)'}`,
                                                        background: isSelected ? 'var(--lgl-success)' : 'var(--index-card-bg)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        transition: 'all 0.2s'
                                                    }}>
                                                        {isSelected && <Check size={14} color="white" strokeWidth={3} />}
                                                    </div>
                                                    <div style={{ flex: 1 }}>
                                                        <div style={{ fontWeight: 850, color: 'var(--index-text-heading)', fontSize: '0.9rem' }}>{cohort.name}</div>
                                                        <div style={{ color: 'var(--index-text-secondary)', fontSize: '0.8rem', fontWeight: 650 }}>
                                                            {cohort.course?.title || 'General Curriculum'}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div style={{ textAlign: 'center', padding: '2.5rem 1.5rem', background: 'var(--index-hover-bg)', borderRadius: '20px', border: '1.5px dashed var(--index-border-color)' }}>
                                            <Info size={24} color="var(--index-text-faint)" style={{ marginBottom: '0.5rem' }} />
                                            <p style={{ color: 'var(--index-text-secondary)', fontWeight: 700, fontSize: '0.85rem', margin: 0 }}>No other cohorts available</p>
                                            <p style={{ color: 'var(--index-text-faint)', fontWeight: 600, fontSize: '0.75rem', marginTop: '4px' }}>This student is already enrolled in all eligible cohorts.</p>
                                        </div>
                                    )}
                                </div>

                                <div className="modal-actions" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                    <button 
                                        className="btn-cancel" 
                                        onClick={() => {
                                            setShowAssignCohortModal(false);
                                            setSelectedCohortIds([]);
                                        }} 
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '44px', margin: 0, fontSize: '0.85rem', fontWeight: 800 }}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        className="btn-confirm" 
                                        disabled={selectedCohortIds.length === 0 || assigningCohorts}
                                        onClick={handleAssignCohorts} 
                                        style={{ 
                                            background: selectedCohortIds.length === 0 ? 'var(--index-text-faint)' : 'var(--lgl-success)', 
                                            color: 'white', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            height: '44px', 
                                            margin: 0, 
                                            fontSize: '0.85rem', 
                                            fontWeight: 850, 
                                            boxShadow: selectedCohortIds.length === 0 ? 'none' : '0 4px 12px color-mix(in srgb, var(--lgl-success) 20%, transparent)',
                                            cursor: selectedCohortIds.length === 0 || assigningCohorts ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {assigningCohorts ? (
                                            <>
                                                <Loader2 className="animate-spin" size={16} style={{ marginRight: '6px' }} />
                                                <span>Assigning...</span>
                                            </>
                                        ) : (
                                            <span>Confirm Assignment ({selectedCohortIds.length})</span>
                                        )}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}
            {showIssueCertModal && selectedCohortForCert && (
                <div className="modal-overlay">
                    <div className="modal-box animate-scale-up" style={{ maxWidth: '550px', borderRadius: '24px', padding: '2.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                        <h3 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 950, color: 'var(--index-text-heading)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {isReassign ? <RotateCcw size={22} color="var(--lgl-warning)" /> : <Award size={22} color="var(--index-primary-color)" />}
                                {isReassign ? 'Reassign Certificate' : 'Issue Verified Certificate'}
                            </h3>
                            <button
                                onClick={() => setShowIssueCertModal(false)}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--index-text-secondary)', display: 'flex', padding: '4px' }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <p style={{ color: 'var(--index-text-secondary)', fontSize: '0.85rem', fontWeight: 600, marginBottom: isReassign ? '1rem' : '2rem' }}>
                            {isReassign
                                ? <>Regenerate the certificate for <strong>{student.name}</strong>. The old certificate will be permanently replaced and a new email will be sent to the student.</>  
                                : <>Generate a manually verified course certificate for <strong>{student.name}</strong>. This will render a verified certificate background, assign a short verification code, and make it available for the student.</>}
                        </p>

                        {isReassign && (
                            <div style={{ background: 'color-mix(in srgb, var(--lgl-warning) 15%, transparent)', border: '1.5px solid color-mix(in srgb, var(--lgl-warning) 35%, transparent)', borderRadius: '12px', padding: '12px 16px', marginBottom: '1.5rem', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                <RotateCcw size={16} color="var(--lgl-warning)" style={{ marginTop: '2px', flexShrink: 0 }} />
                                <p style={{ margin: 0, fontSize: '0.8rem', fontWeight: 700, color: 'var(--lgl-warning)', lineHeight: 1.5 }}>
                                    The existing certificate image will be deleted from the CDN and replaced. The verification UUID is preserved so old links remain valid.
                                </p>
                            </div>
                        )}

                        <div style={{ display: 'grid', gap: '1.5rem', marginBottom: '2.5rem' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 900, color: 'var(--index-text-heading)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                                    Recipient Full Name
                                </label>
                                <input
                                    type="text"
                                    value={certForm.fullName}
                                    onChange={(e) => setCertForm({ ...certForm, fullName: e.target.value })}
                                    style={{
                                        width: '100%',
                                        height: '46px',
                                        background: 'var(--index-hover-bg)',
                                        border: '1.5px solid var(--index-border-color)',
                                        borderRadius: '12px',
                                        padding: '0 1rem',
                                        fontSize: '0.95rem',
                                        fontWeight: 600,
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div>
                                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 900, color: 'var(--index-text-heading)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                                    Course Title
                                </label>
                                <input
                                    type="text"
                                    value={certForm.courseTitle}
                                    onChange={(e) => setCertForm({ ...certForm, courseTitle: e.target.value })}
                                    style={{
                                        width: '100%',
                                        height: '46px',
                                        background: 'var(--index-hover-bg)',
                                        border: '1.5px solid var(--index-border-color)',
                                        borderRadius: '12px',
                                        padding: '0 1rem',
                                        fontSize: '0.95rem',
                                        fontWeight: 600,
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 900, color: 'var(--index-text-heading)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                                        Issue Date
                                    </label>
                                    <input
                                        type="date"
                                        value={certForm.issuedAt}
                                        onChange={(e) => setCertForm({ ...certForm, issuedAt: e.target.value })}
                                        style={{
                                            width: '100%',
                                            height: '46px',
                                            background: 'var(--index-hover-bg)',
                                            border: '1.5px solid var(--index-border-color)',
                                            borderRadius: '12px',
                                            padding: '0 1rem',
                                            fontSize: '0.95rem',
                                            fontWeight: 600,
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                                <div>
                                    <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 900, color: 'var(--index-text-heading)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                                        Issued By
                                    </label>
                                    <input
                                        type="text"
                                        value={certForm.issuedBy}
                                        onChange={(e) => setCertForm({ ...certForm, issuedBy: e.target.value })}
                                        style={{
                                            width: '100%',
                                            height: '46px',
                                            background: 'var(--index-hover-bg)',
                                            border: '1.5px solid var(--index-border-color)',
                                            borderRadius: '12px',
                                            padding: '0 1rem',
                                            fontSize: '0.95rem',
                                            fontWeight: 600,
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="modal-actions" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                            <button
                                className="btn-cancel"
                                onClick={() => setShowIssueCertModal(false)}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '44px', margin: 0 }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn-confirm"
                                disabled={issuingCert || !certForm.fullName.trim() || !certForm.courseTitle.trim()}
                                onClick={handleIssueCertificate}
                                style={{
                                    background: isReassign ? 'var(--lgl-warning)' : 'var(--index-primary-color)',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    height: '44px',
                                    margin: 0,
                                    boxShadow: isReassign ? '0 4px 12px color-mix(in srgb, var(--lgl-warning) 20%, transparent)' : '0 4px 12px color-mix(in srgb, var(--index-primary-color) calc(0.2 * 100%), transparent)'
                                }}
                            >
                                {issuingCert ? (
                                    <>
                                        <Loader2 className="animate-spin" size={16} style={{ marginRight: '6px' }} />
                                        <span>{isReassign ? 'Reassigning...' : 'Generating...'}</span>
                                    </>
                                ) : (
                                    <span>{isReassign ? 'Reassign Certificate' : 'Issue Certificate'}</span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Receipt Inspection Lightbox Modal */}
            {viewingReceiptModal && (
                <div
                    className="modal-overlay"
                    onClick={() => setViewingReceiptModal(null)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(15, 23, 42, 0.85)',
                        backdropFilter: 'blur(8px)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1.5rem'
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: '#ffffff',
                            borderRadius: '24px',
                            maxWidth: '850px',
                            width: '100%',
                            maxHeight: '90vh',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.35)'
                        }}
                    >
                        <div style={{
                            padding: '1.25rem 1.5rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid #e2e8f0',
                            background: '#f8fafc'
                        }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 850, color: '#0f172a' }}>
                                    {student?.payment_method === 'square' ? 'Square Payment Receipt Preview' : student?.payment_method === 'zelle' ? 'Zelle Payment Receipt Preview' : 'Payment Receipt Preview'}
                                </h3>
                                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                    Student: {student?.name} ({student?.email})
                                </span>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <button
                                    type="button"
                                    onClick={handleOpenSendReceiptModal}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '0.45rem 0.85rem',
                                        borderRadius: '8px',
                                        background: 'var(--index-primary-color, #1a4d3e)',
                                        border: 'none',
                                        color: '#ffffff',
                                        fontSize: '0.8rem',
                                        fontWeight: 800,
                                        cursor: 'pointer'
                                    }}
                                >
                                    <Mail size={14} /> Send Receipt to Email
                                </button>
                                <a
                                    href={viewingReceiptModal}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        padding: '0.45rem 0.85rem',
                                        borderRadius: '8px',
                                        background: '#ffffff',
                                        border: '1px solid #cbd5e1',
                                        color: '#334155',
                                        textDecoration: 'none',
                                        fontSize: '0.8rem',
                                        fontWeight: 800
                                    }}
                                >
                                    <ExternalLink size={14} /> Open Original
                                </a>
                                <button
                                    onClick={() => setViewingReceiptModal(null)}
                                    style={{
                                        width: '36px',
                                        height: '36px',
                                        borderRadius: '50%',
                                        background: '#fee2e2',
                                        color: '#ef4444',
                                        border: 'none',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <X size={18} />
                                </button>
                            </div>
                        </div>

                        <div style={{
                            padding: '1.5rem',
                            overflowY: 'auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: '#0f172a'
                        }}>
                            <img
                                src={viewingReceiptModal}
                                alt="Proof of Payment"
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '70vh',
                                    objectFit: 'contain',
                                    borderRadius: '8px',
                                    boxShadow: '0 8px 30px rgba(0,0,0,0.5)'
                                }}
                            />
                        </div>
                    </div>
                </div>
            )}
            {/* Send Receipt Email Modal */}
            {showSendReceiptModal && (
                <div
                    className="modal-overlay"
                    onClick={() => setShowSendReceiptModal(false)}
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(15, 23, 42, 0.85)',
                        backdropFilter: 'blur(8px)',
                        zIndex: 10000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '1.5rem'
                    }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            background: 'var(--index-card-bg, #ffffff)',
                            borderRadius: '24px',
                            maxWidth: '600px',
                            width: '100%',
                            maxHeight: '92vh',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            boxShadow: '0 25px 60px rgba(0, 0, 0, 0.35)',
                            border: '1.5px solid var(--index-border-color, #e2e8f0)'
                        }}
                    >
                        {/* Header */}
                        <div style={{
                            padding: '1.25rem 1.5rem',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid var(--index-border-subtle, #e2e8f0)',
                            background: 'var(--index-hover-bg, #f8fafc)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{
                                    width: '42px',
                                    height: '42px',
                                    borderRadius: '12px',
                                    background: 'color-mix(in srgb, var(--index-primary-color, #1a4d3e) 15%, transparent)',
                                    color: 'var(--index-primary-color, #1a4d3e)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    <Receipt size={22} />
                                </div>
                                <div>
                                    <h3 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 900, color: 'var(--index-text-heading, #0f172a)' }}>
                                        Send Payment Receipt Email
                                    </h3>
                                    <span style={{ fontSize: '0.8rem', color: 'var(--index-text-faint, #64748b)' }}>
                                        Official Payment Record & Invoice for {student?.name}
                                    </span>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowSendReceiptModal(false)}
                                style={{
                                    width: '34px',
                                    height: '34px',
                                    borderRadius: '50%',
                                    background: 'var(--index-hover-bg, #f1f5f9)',
                                    color: 'var(--index-text-secondary, #64748b)',
                                    border: 'none',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                <X size={18} />
                            </button>
                        </div>

                        {/* Modal Body / Form */}
                        <form onSubmit={handleSendReceiptEmail} style={{ padding: '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                            
                            {/* Student Record Quick Summary */}
                            <div style={{
                                padding: '1rem 1.25rem',
                                borderRadius: '16px',
                                background: 'var(--index-hover-bg, #f8fafc)',
                                border: '1.5px solid var(--index-border-subtle, #e2e8f0)',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.5rem'
                            }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--index-text-faint)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Student & Program
                                    </span>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 800, color: 'var(--lgl-success, #166534)', background: 'color-mix(in srgb, var(--lgl-success) 12%, transparent)', padding: '2px 8px', borderRadius: '6px' }}>
                                        Receipt #LGL-{String(student?.id || 1).padStart(5, '0')}
                                    </span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                    <strong style={{ fontSize: '0.95rem', color: 'var(--index-text-heading)' }}>
                                        {student?.name}
                                    </strong>
                                    <span style={{ fontSize: '0.8rem', color: 'var(--index-text-secondary)' }}>
                                        {student?.email}
                                    </span>
                                </div>
                                <div style={{ fontSize: '0.8rem', color: 'var(--index-text-secondary)', borderTop: '1px dashed var(--index-border-color, #e2e8f0)', paddingTop: '6px', marginTop: '2px', display: 'flex', justifyContent: 'space-between' }}>
                                    <span>Course: <strong>{student?.course_name || 'Project Controls & Scheduling Masterclass'}</strong></span>
                                    <span style={{ color: 'var(--lgl-success)', fontWeight: 800 }}>
                                        {student?.payment_plan === 'installment' || student?.payment_plan === '50_percent' || student?.payment_plan === '50%' ? '50% Installment' : '100% Full Payment'}
                                    </span>
                                </div>
                            </div>

                            {/* Recipient Email Field */}
                            <div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                    <label style={{ fontSize: '0.85rem', fontWeight: 800, color: 'var(--index-text-heading)' }}>
                                        Recipient Email Address(es) <span style={{ color: 'var(--lgl-error)' }}>*</span>
                                    </label>
                                    <span style={{ fontSize: '0.72rem', color: 'var(--index-text-faint)' }}>
                                        Separate multiple with comma
                                    </span>
                                </div>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type="text"
                                        required
                                        placeholder="admin@layosgroupllc.com, finance@company.com"
                                        value={receiptRecipientEmail}
                                        onChange={(e) => setReceiptRecipientEmail(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: '0.75rem 1rem',
                                            borderRadius: '12px',
                                            border: '1.5px solid var(--index-border-color, #cbd5e1)',
                                            background: 'var(--index-card-bg, #ffffff)',
                                            color: 'var(--index-text-heading)',
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                            boxSizing: 'border-box'
                                        }}
                                    />
                                </div>

                                {/* Quick chips to fill/append email */}
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px', alignItems: 'center' }}>
                                    <span style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--index-text-faint)' }}>Quick Fill:</span>
                                    {user?.email && (
                                        <button
                                            type="button"
                                            onClick={() => setReceiptRecipientEmail(user.email)}
                                            style={{
                                                fontSize: '0.72rem',
                                                fontWeight: 750,
                                                padding: '3px 8px',
                                                borderRadius: '6px',
                                                background: 'var(--index-hover-bg, #f1f5f9)',
                                                border: '1px solid var(--index-border-color, #e2e8f0)',
                                                color: 'var(--index-primary-color)',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            + My Email ({user.email})
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setReceiptRecipientEmail('layosgroupllc@gmail.com')}
                                        style={{
                                            fontSize: '0.72rem',
                                            fontWeight: 750,
                                            padding: '3px 8px',
                                            borderRadius: '6px',
                                            background: 'var(--index-hover-bg, #f1f5f9)',
                                            border: '1px solid var(--index-border-color, #e2e8f0)',
                                            color: 'var(--index-text-secondary)',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        + Layos Admin (layosgroupllc@gmail.com)
                                    </button>
                                    {student?.email && (
                                        <button
                                            type="button"
                                            onClick={() => setReceiptRecipientEmail(student.email)}
                                            style={{
                                                fontSize: '0.72rem',
                                                fontWeight: 750,
                                                padding: '3px 8px',
                                                borderRadius: '6px',
                                                background: 'var(--index-hover-bg, #f1f5f9)',
                                                border: '1px solid var(--index-border-color, #e2e8f0)',
                                                color: 'var(--index-text-secondary)',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            + Student ({student.email})
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Custom Record / Internal Note */}
                            <div>
                                <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 800, color: 'var(--index-text-heading)', marginBottom: '6px' }}>
                                    Administrative Record Note (Optional)
                                </label>
                                <textarea
                                    rows={3}
                                    placeholder="e.g. Generated for company tuition reimbursement / internal tax accounting records..."
                                    value={receiptCustomNote}
                                    onChange={(e) => setReceiptCustomNote(e.target.value)}
                                    style={{
                                        width: '100%',
                                        padding: '0.75rem 1rem',
                                        borderRadius: '12px',
                                        border: '1.5px solid var(--index-border-color, #cbd5e1)',
                                        background: 'var(--index-card-bg, #ffffff)',
                                        color: 'var(--index-text-heading)',
                                        fontSize: '0.85rem',
                                        fontWeight: 500,
                                        resize: 'vertical',
                                        boxSizing: 'border-box'
                                    }}
                                />
                            </div>

                            {/* Also Send to Student Checkbox */}
                            <div style={{
                                padding: '0.85rem 1rem',
                                borderRadius: '12px',
                                background: 'var(--index-hover-bg, #f8fafc)',
                                border: '1px solid var(--index-border-subtle, #e2e8f0)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px'
                            }}>
                                <input
                                    type="checkbox"
                                    id="receipt-send-student"
                                    checked={receiptSendToStudent}
                                    onChange={(e) => setReceiptSendToStudent(e.target.checked)}
                                    style={{ width: '18px', height: '18px', accentColor: 'var(--index-primary-color)', cursor: 'pointer' }}
                                />
                                <label htmlFor="receipt-send-student" style={{ fontSize: '0.825rem', fontWeight: 750, color: 'var(--index-text-heading)', cursor: 'pointer' }}>
                                    Also send a copy of this official receipt to the student ({student?.email})
                                </label>
                            </div>

                            {/* Footer Buttons */}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '0.5rem' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowSendReceiptModal(false)}
                                    disabled={sendingReceipt}
                                    style={{
                                        padding: '0.75rem 1.4rem',
                                        borderRadius: '12px',
                                        background: 'var(--index-hover-bg, #f1f5f9)',
                                        border: '1px solid var(--index-border-color, #cbd5e1)',
                                        color: 'var(--index-text-secondary)',
                                        fontWeight: 800,
                                        fontSize: '0.875rem',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={sendingReceipt}
                                    style={{
                                        padding: '0.75rem 1.6rem',
                                        borderRadius: '12px',
                                        background: 'linear-gradient(135deg, var(--index-primary-color, #1a4d3e) 0%, #2d7a5f 100%)',
                                        border: 'none',
                                        color: '#ffffff',
                                        fontWeight: 850,
                                        fontSize: '0.875rem',
                                        cursor: sendingReceipt ? 'not-allowed' : 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        boxShadow: '0 4px 14px color-mix(in srgb, var(--index-primary-color) 30%, transparent)'
                                    }}
                                >
                                    {sendingReceipt ? (
                                        <>
                                            <Loader2 size={16} className="animate-spin" />
                                            <span>Generating & Sending...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Send size={16} />
                                            <span>Send Official Receipt</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
